import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProductCartCounter from '../components/ProductCartCounter';
import SalonHeaderActions from '../components/SalonHeaderActions';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import SkinHealthScoresPanel from '../components/report/SkinHealthScoresPanel';
import SkinReportHero from '../components/report/SkinReportHero';
import SkinDetailedCrops from '../components/report/SkinDetailedCrops';
import SkinMacroCloseLooks from '../components/report/SkinMacroCloseLooks';
import SkinMacroAnalysisReport from '../components/report/SkinMacroAnalysisReport';
import SkinReportRoutine from '../components/report/SkinReportRoutine';
import SkinReportTips from '../components/report/SkinReportTips';
import SkinConcernsPriority from '../components/report/SkinConcernsPriority';
import ReportPdfQrPanel from '../../shared/ReportPdfQrPanel';
import {
  sortConcernsBySeverity,
  formatSkinColorLabel,
  computeImprovementPotential,
  buildFallbackTips,
  sanitizeTipText,
} from '../utils/reportSeverity';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { persistFranchiseBrand, readStoredFranchiseBrandName } from '../../shared/franchiseBrand';
import { applyPageSetupResult, storePageSetupData } from '../components/PageBackgroundSync';
import { saveAqiUpdate } from '../utils/dataSaver';
import { useSkinSession } from '../context/SkinSessionContext';
import { getSessionIdFromUrl, getSessionId, pathWithSessionId } from '../utils/sessionManager';
import { goSalonHubForNewUser } from '../utils/salonHomeNav';
import { saveSkinFlowState, getSkinFlowState } from '../utils/skinFlowState';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { createSalonReportPdfDownloadUrl } from '../utils/salonReportPdfQr';
import { buildSalonReportPdfPayload } from '../utils/buildSalonReportPdfPayload';
import { resolveSalonMediaUrl } from '../utils/mediaUrl';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { parseSegmentationPayload, isValidProductUrl, resolveProductHref } from '../utils/segmentation';
import { getConditionDisplayName } from '../config/skinConditionDisplayNames';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { fetchSkinAnalysisBundle } from '../utils/skinAnalysisBundle';
import { persistProductCart, persistTreatmentCart } from '../utils/cartPersistence';
import { modernConfirm } from '../../admin-shared/modernDialog';
import { getPreviousSkinAnalysis } from '../utils/previousSkinAnalysis';
import {
  buildConcernItemsList,
  buildConcernItemsFromPreviousPayload,
  buildConcernRadarItemsFromList,
  isPreviousScanComparable,
  resolveConcernKey,
} from '../utils/concernRadarBuilder';
import { normalizeConditionName, normalizeDescriptionMap, normalizeDetections } from '../utils/skinConditionNormalization';
import { getDetectionsToDraw, canonicalSkinConditionName, isSkinConditionName, getColorByClassId } from '../utils/skinResultsUtils';
import { getSideCaptures } from '../utils/sideCaptureStore';
import { fetchSkinConcernMask } from '../utils/lazySkinMask';
import { getSkinConditionKeyCandidates } from '../utils/maskUtils';
import { fetchStoredConditionDescriptions, fetchStoredVisionInsight, fetchSkinMacroResult, requestSkinAge } from '../utils/skinAnalysisApi';
import { buildRecommendableConditions, buildRecommendationPayloadScores } from '../utils/recommendationSeverity';
import { resolvePresentationGuestGenderForApi } from '../../presentation/presentationRecommendations';
import { getFranchiseOrganizationId, getFranchiseId, appendFranchiseThemeParams } from '../../shared/franchiseFlowAuth';
import { readSelectedServiceNames } from '../utils/visitHandoff';
import {
  scheduleSyncAnalysisPendingServices,
  treatmentCartToPendingServices,
  confirmAnalysisServicesAndStart,
  syncAnalysisProductCartToCheckin,
  syncAnalysisRecommendedProductsReady,
} from '../utils/analysisServiceSync';
import {
  getSalonRoutineCache,
  setSalonRoutineCache,
  getSalonAqiCache,
  setSalonAqiCache,
  getSalonRecommendationStatusCache,
  setSalonRecommendationStatusCache,
} from '../utils/salonVisitMemoryCache';
import { salonGenderDisplayLabel } from '../utils/salonGender';
import { resolveGuestUserDataForAnalysis } from '../utils/bootstrapFaceSalonSession';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';
import './SkinRoutinePage.css';

const parseFeatureToggle = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'yes' || v === 'true' || v === '1' || v === 'on';
  }
  return false;
};

const toYesNoValue = (value) => {
  if (value == null) return null;
  return parseFeatureToggle(value) ? 'yes' : 'no';
};

const hasRecommendedProductsPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return false;
  const morning = Array.isArray(payload.morning) ? payload.morning : [];
  const evening = Array.isArray(payload.evening) ? payload.evening : [];
  return morning.length > 0 || evening.length > 0;
};

const hasRecommendedTreatmentsPayload = (payload) => {
  return Array.isArray(payload) && payload.length > 0;
};

const treatmentRowCartKey = (item, idx) => {
  if (item?.id != null) return `treatment_${item.id}`;
  if (item?.treatment_id != null) return `treatment_${item.treatment_id}`;
  const stableName = String(item?.treatment || item?.name || '').trim();
  if (stableName) return `treatment_${stableName.replace(/\s+/g, '_').toLowerCase()}`;
  return `treatment_${idx}_row`;
};

// Helper function to get outline color based on score range

function isUsableSkinAgeExplanation(text) {
  const cleaned = String(text || '').trim();
  if (cleaned.length < 12) return false;
  if (['_', '-', 'n/a', 'na', 'none', 'null', 'tbd'].includes(cleaned.toLowerCase())) return false;
  return /[a-z]/i.test(cleaned);
}

function isCompleteSkinAgeRecord(record) {
  if (!record || typeof record !== 'object') return false;
  const recordType = record.type || 'visual_estimate';
  if (recordType !== 'visual_estimate') return false;
  const age = record.estimated_age ?? record.skin_age;
  if (age == null || Number.isNaN(Number(age))) return false;
  return isUsableSkinAgeExplanation(record.explanation);
}

function applyVisionInsightPayload(data, { buildSkinAgeExplanationFallback }) {
  if (!data || typeof data !== 'object') return false;
  const estimated = data.estimated_age ?? data.skin_age;
  if (estimated == null) return false;
  const explanation = isUsableSkinAgeExplanation(data.explanation)
    ? String(data.explanation).trim()
    : buildSkinAgeExplanationFallback(estimated);
  if (!isUsableSkinAgeExplanation(explanation)) return false;

  const skinAgeRecord = {
    skin_age: estimated,
    estimated_age: estimated,
    type: data.type || 'visual_estimate',
    confidence: data.confidence,
    explanation,
  };
  const photoInsightRecord = data.photo_insight && typeof data.photo_insight === 'object'
    ? data.photo_insight
    : (data.ai_skin_type ? { skin_type: data.ai_skin_type } : null);
  const envTipRecord = typeof data.env_tip === 'string' && isUsableEnvTip(data.env_tip)
    ? data.env_tip.trim()
    : null;

  return { skinAgeRecord, photoInsightRecord, envTipRecord };
}

function buildSkinAgeExplanationFallback(estimatedAge) {
  const age = Number(estimatedAge);
  if (!Number.isFinite(age)) return '';
  return `Your skin age of ${age} is based on visible texture, tone, and radiance in your front-face photo.`;
}

function formatVisionSkinType(type) {
  const t = String(type || '').trim().toLowerCase();
  if (!t || t === 'unknown') return null;
  return t.charAt(0).toUpperCase() + t.slice(1);
}


function isUsableEnvTip(text) {
  const cleaned = String(text || '').trim();
  if (cleaned.length < 16 || cleaned.length > 220) return false;
  const lower = cleaned.toLowerCase();
  if (lower.includes('thinking process') || lower.includes('analyze user') || lower.includes('json')) return false;
  if (cleaned.startsWith('{') || cleaned.includes('"skin_type"') || cleaned.includes('skin_type":')) return false;
  if (!/[a-z]/i.test(cleaned)) return false;
  return true;
}

const SkinRoutinePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, tokenReady } = useSkinSession();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();

  useEffect(() => {
    let cancelled = false;
    const loadOrgName = async () => {
      if (!tokenReady || !token) return;
      try {
        const qs = appendFranchiseThemeParams({ _: String(Date.now()) });
        const pageSetupUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup?${qs}`;
        const res = await fetchWithTimeout(pageSetupUrl, { timeoutMs: 15000 });
        if (!res.ok) return;
        const result = await res.json();
        if (cancelled) return;
        applyPageSetupResult(result);
        storePageSetupData(token, result);
        const ext = result?.external_fields || result?.data?.external_fields || {};
        // Franchise outlet name wins over HQ organization_name from theme packs.
        const outletName = String(
          ext.outlet_name || ext.franchise_name || ext.salon_name || ''
        ).trim();
        const orgFallback = String(
          ext.organization_name || ext.org_name || result?.organization_name || ''
        ).trim();
        const storedOutlet = readStoredFranchiseBrandName();
        const name = outletName || storedOutlet || orgFallback;
        if (name) {
          setOrgName(name);
          persistFranchiseBrand({ name });
        }
      } catch (_) { /* keep stored */ }
    };
    void loadOrgName();
    return () => { cancelled = true; };
  }, [token, tokenReady]);

  const sessionIdFromState = location.state?.sessionId || null;
  const sessionIdFromUrl = getSessionIdFromUrl(location);
  const sessionIdFromStorage = getSessionId();
  const sessionId = sessionIdFromState || sessionIdFromUrl || sessionIdFromStorage || null;

  const effectiveState = useMemo(() => {
    const stored = sessionId ? (getSkinFlowState(sessionId) || {}) : {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    const definedPatch = Object.fromEntries(Object.entries(fromLocation).filter(([, v]) => v !== undefined));
    if ('sideCaptures' in definedPatch) {
      const sc = definedPatch.sideCaptures;
      const hasAny = !!(sc && typeof sc === 'object' && (sc.left || sc.right));
      if (!hasAny) delete definedPatch.sideCaptures;
    }
    const mergedSkinResults = {
      ...(stored.skinResults && typeof stored.skinResults === 'object' ? stored.skinResults : {}),
      ...(definedPatch.skinResults && typeof definedPatch.skinResults === 'object' ? definedPatch.skinResults : {}),
    };
    const out = { ...stored, ...definedPatch, sessionId: sessionId || definedPatch.sessionId };
    if (Object.keys(mergedSkinResults).length > 0) {
      out.skinResults = mergedSkinResults;
    }
    return out;
  }, [location.state, sessionId]);

  const macroSkipped = Boolean(effectiveState?.macroSkipped);
  const userDataFromState = useMemo(() => {
    const merged = resolveGuestUserDataForAnalysis({
      ...(effectiveState && typeof effectiveState === 'object' ? effectiveState : {}),
      userData: effectiveState?.userData,
    });
    return merged && (merged.name || merged.mobileNumber || merged.gender || merged.skinType)
      ? merged
      : effectiveState?.userData || null;
  }, [effectiveState]);
  const capturedImageFromState = effectiveState?.capturedImage || null;
  const skinResultsFromState = effectiveState?.skinResults || null;
  const visibleConditionNames = effectiveState?.visibleConditionNames ?? null;
  const routineDataFromState = effectiveState?.routineData || null;
  const forceRegenerateForNewScan = Boolean(effectiveState?.forceRegenerateForNewScan);
  const ROUTINE_CACHE_VERSION = 2;

  const hasUsableRoutineData = (data) => {
    if (!data || typeof data !== 'object') return false;
    const hasSummary = typeof data.summary === 'string' && data.summary.trim().length > 0;
    const morning = Array.isArray(data.routine?.morning) ? data.routine.morning : [];
    const evening = Array.isArray(data.routine?.evening) ? data.routine.evening : [];
    return hasSummary || morning.length > 0 || evening.length > 0;
  };

  const getStoredRoutineData = () => {
    // Prefetch from analysis loader wins even on a new scan.
    if (hasUsableRoutineData(routineDataFromState)) return routineDataFromState;
    if (forceRegenerateForNewScan) return null;
    if (sessionId) {
      const stored = getSalonRoutineCache(sessionId);
      if (stored) {
        if (stored?.cacheVersion !== ROUTINE_CACHE_VERSION) return null;
        if (hasUsableRoutineData(stored)) return stored;
      }
    }
    return null;
  };

  const storedData = getStoredRoutineData();
  const getStoredRecommendationStatus = () => {
    if (forceRegenerateForNewScan) {
      return { treatmentReady: false, productReady: false };
    }
    const fromFlow = effectiveState?.recommendationStatus;
    if (fromFlow && typeof fromFlow === 'object') {
      return {
        treatmentReady: Boolean(fromFlow.treatmentReady),
        productReady: Boolean(fromFlow.productReady),
      };
    }
    if (!sessionId) {
      return { treatmentReady: false, productReady: false };
    }
    const stored = getSalonRecommendationStatusCache(sessionId);
    if (!stored) return { treatmentReady: false, productReady: false };
    return {
      treatmentReady: Boolean(stored.treatmentReady),
      productReady: Boolean(stored.productReady),
    };
  };

  const storedRecommendationStatus = getStoredRecommendationStatus();
  const cameBackWithRecommendations = !!(location.state && typeof location.state === 'object'
    && ('treatmentCartItems' in location.state || 'cartItems' in location.state));
  const [summary, setSummary] = useState(storedData?.summary || '');
  const [routine, setRoutine] = useState(storedData?.routine || { morning: [], evening: [] });
  const [reportInsight, setReportInsight] = useState(() => {
    const fromRoutine = storedData?.routine?.insight;
    return typeof fromRoutine === 'string' ? fromRoutine : (storedData?.insight || '');
  });
  const [reportTips, setReportTips] = useState(() => {
    const fromRoutine = storedData?.routine?.tips;
    const raw = Array.isArray(fromRoutine)
      ? fromRoutine
      : (Array.isArray(storedData?.tips) ? storedData.tips : []);
    return raw
      .map((t) => sanitizeTipText(String(t || '').trim()))
      .filter((t) => t && t.length > 8)
      .slice(0, 6);
  });
  const [orgName, setOrgName] = useState(() => readStoredFranchiseBrandName());
  const [skinAge, setSkinAge] = useState(() => {
    if (isCompleteSkinAgeRecord(storedData?.skinAge)) return storedData.skinAge;
    const fromNav = applyVisionInsightPayload(effectiveState?.visionInsight, {
      buildSkinAgeExplanationFallback,
    });
    return fromNav?.skinAgeRecord || null;
  });
  const [photoInsight, setPhotoInsight] = useState(() => {
    if (storedData?.photoInsight) return storedData.photoInsight;
    const fromNav = applyVisionInsightPayload(effectiveState?.visionInsight, {
      buildSkinAgeExplanationFallback,
    });
    return fromNav?.photoInsightRecord || null;
  });
  const [envTip, setEnvTip] = useState(() => {
    if (storedData?.envTip) return storedData.envTip;
    const fromNav = applyVisionInsightPayload(effectiveState?.visionInsight, {
      buildSkinAgeExplanationFallback,
    });
    return fromNav?.envTipRecord || null;
  });
  const [macroAnalysis, setMacroAnalysis] = useState(
    () => effectiveState?.macroAnalysis || storedData?.macroAnalysis || null
  );
  const [isMacroLoading, setIsMacroLoading] = useState(() => {
    if (macroSkipped) return false;
    const alreadyReady = Boolean(
      (effectiveState?.macroAnalysis?.regions && typeof effectiveState.macroAnalysis.regions === 'object')
      || (storedData?.macroAnalysis?.regions && typeof storedData.macroAnalysis.regions === 'object')
    );
    if (alreadyReady) return false;
    let fromStorage = false;
    if (sessionId) {
      try {
        fromStorage = sessionStorage.getItem(`salon-macro-pending:${sessionId}`) === '1';
      } catch {
        fromStorage = false;
      }
    }
    return Boolean(effectiveState?.macroPending || fromStorage);
  });

  const clearMacroPendingFlag = useCallback(() => {
    if (!sessionId) return;
    try {
      sessionStorage.removeItem(`salon-macro-pending:${sessionId}`);
    } catch {
      /* ignore */
    }
  }, [sessionId]);

  useEffect(() => {
    if (macroSkipped) {
      setIsMacroLoading(false);
      clearMacroPendingFlag();
      return;
    }
    if (macroAnalysis?.regions || effectiveState?.macroAnalysis?.regions) {
      setIsMacroLoading(false);
      clearMacroPendingFlag();
      return;
    }
    if (effectiveState?.macroPending) {
      setIsMacroLoading(true);
    }
  }, [
    clearMacroPendingFlag,
    effectiveState?.macroAnalysis?.regions,
    effectiveState?.macroPending,
    macroAnalysis?.regions,
    macroSkipped,
  ]);

  useEffect(() => {
    if (!sessionId || !macroSkipped) return;
    clearMacroPendingFlag();
    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, { ...current, sessionId, macroSkipped: true });
  }, [sessionId, macroSkipped, clearMacroPendingFlag]);

  useEffect(() => {
    if (effectiveState?.macroAnalysis?.regions) {
      setMacroAnalysis(effectiveState.macroAnalysis);
      setIsMacroLoading(false);
      clearMacroPendingFlag();
    }
  }, [effectiveState?.macroAnalysis, clearMacroPendingFlag]);

  useEffect(() => {
    const onMacroReady = (event) => {
      const detail = event?.detail;
      if (!detail?.macro?.regions) return;
      if (sessionId && String(detail.sessionId) !== String(sessionId)) return;
      setMacroAnalysis(detail.macro);
      setIsMacroLoading(false);
      clearMacroPendingFlag();
    };
    window.addEventListener('salon-macro-analysis-ready', onMacroReady);
    return () => window.removeEventListener('salon-macro-analysis-ready', onMacroReady);
  }, [sessionId, clearMacroPendingFlag]);

  // Macro runs in the background after navigation — poll stored result until it lands.
  useEffect(() => {
    if (!sessionId || macroSkipped) return undefined;
    if (macroAnalysis?.regions) {
      setIsMacroLoading(false);
      clearMacroPendingFlag();
      return undefined;
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40; // ~80s at 2s
    const poll = async () => {
      attempts += 1;
      try {
        const macro = await fetchSkinMacroResult({
          sessionId,
          appSessionToken: token || null,
        });
        if (!cancelled && macro?.regions) {
          setMacroAnalysis(macro);
          setIsMacroLoading(false);
          clearMacroPendingFlag();
          return true;
        }
      } catch {
        /* non-blocking */
      }
      if (!cancelled && attempts >= maxAttempts) {
        setIsMacroLoading(false);
        clearMacroPendingFlag();
      }
      return false;
    };
    poll();
    const timer = window.setInterval(async () => {
      if (cancelled || attempts >= maxAttempts) {
        window.clearInterval(timer);
        return;
      }
      const done = await poll();
      if (done) window.clearInterval(timer);
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [sessionId, token, macroAnalysis?.regions, clearMacroPendingFlag, macroSkipped]);
  const [isSkinAgeLoading, setIsSkinAgeLoading] = useState(() => (
    !isCompleteSkinAgeRecord(
      storedData?.skinAge
      || applyVisionInsightPayload(effectiveState?.visionInsight, {
        buildSkinAgeExplanationFallback,
      })?.skinAgeRecord
    )
  ));
  const [recommendedTreatments, setRecommendedTreatments] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState({ morning: [], evening: [] });
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [treatmentCartItems, setTreatmentCartItems] = useState(
    () => (effectiveState?.treatmentCartItems && typeof effectiveState.treatmentCartItems === 'object' ? effectiveState.treatmentCartItems : {})
  );
  const [productCartItems, setProductCartItems] = useState(
    () => (effectiveState?.cartItems && typeof effectiveState.cartItems === 'object' ? effectiveState.cartItems : {})
  );
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmNotice, setConfirmNotice] = useState('');
  const [showHomeConfirmPopup, setShowHomeConfirmPopup] = useState(false);
  const [aboutPopup, setAboutPopup] = useState({ open: false, title: '', text: '', sections: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // When "no", hide AI Summary section and do not show/generate it
  const [aiGeneratedSummary, setAiGeneratedSummary] = useState(null);
  const [segmentationAiMode, setSegmentationAiMode] = useState(null);
  const [aiGeneratedRoutine, setAiGeneratedRoutine] = useState(null);
  const [uspPopup, setUspPopup] = useState({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [serviceSuggestionEnabled, setServiceSuggestionEnabled] = useState(false);
  const [productSuggestionEnabled, setProductSuggestionEnabled] = useState(false);
  const [addToCartEnabled, setAddToCartEnabled] = useState(false);
  const [productLinkEnabled, setProductLinkEnabled] = useState(false);
  const [showAdvancedCharts, setShowAdvancedCharts] = useState(false);
  const [showSkinConcerns, setShowSkinConcerns] = useState(true);
  const [showSkinConditions, setShowSkinConditions] = useState(true);
  const [suggestConfigLoaded, setSuggestConfigLoaded] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [productRecommendationSource, setProductRecommendationSource] = useState('database');
  const [productsPageUrl, setProductsPageUrl] = useState('');
  const [productsPageUrls, setProductsPageUrls] = useState([]);
  const [selectedConcernName, setSelectedConcernName] = useState(() => effectiveState?.selectedCondition || null);
  const [pdfCaptureMode, setPdfCaptureMode] = useState(false);
  const pdfCaptureRestoreRef = useRef({ concern: null, view: 'front' });
  const [restoredSideCaptures, setRestoredSideCaptures] = useState(() => effectiveState?.sideCaptures || null);
  const [selectedConcernView, setSelectedConcernView] = useState(() => {
    const view = effectiveState?.imageView;
    return view === 'left' || view === 'right' || view === 'front' ? view : 'front';
  });
  const [selectedConcernPreviewImage, setSelectedConcernPreviewImage] = useState(
    () => skinResultsFromState?.markedImage || capturedImageFromState || null
  );
  const [aiConditionDescriptions, setAiConditionDescriptions] = useState(() => {
    const seeded = effectiveState?.aiConditionDescriptions;
    return seeded && typeof seeded === 'object' ? seeded : {};
  });
  const [, setIsLoadingConditionDescriptions] = useState(false);
  const [previousSkinData, setPreviousSkinData] = useState(() => effectiveState?.previousSkinData || null);
  const [radarComparisonTab, setRadarComparisonTab] = useState('after');
  const [radarConcernModal, setRadarConcernModal] = useState({ open: false, index: 0 });
  const [isRadarEnlargedOpen, setIsRadarEnlargedOpen] = useState(false);
  const [recommendationGenerationState, setRecommendationGenerationState] = useState({
    inProgress: false,
    treatmentReady: cameBackWithRecommendations || storedRecommendationStatus.treatmentReady,
    productReady: cameBackWithRecommendations || storedRecommendationStatus.productReady,
    error: null,
  });
  const scrollShellRef = useRef(null);
  const scrollMainRef = useRef(null);
  const concernPreviewCanvasRef = useRef(null);
  const radarModalTouchStartXRef = useRef(null);
  const recommendGenerationStartedRef = useRef(false);
  const treatmentsHydrateStartedRef = useRef(false);
  const treatmentsHydrateSessionRef = useRef(null);
  const servicePreselectDoneRef = useRef(false);
  const aqiDataFromState = effectiveState?.aqiData ?? null;
  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';
  const sideCaptures = effectiveState?.sideCaptures || restoredSideCaptures || null;
  const sideResultsRaw = skinResultsFromState?.sideResults;
  const sideResults = useMemo(() => (Array.isArray(sideResultsRaw) ? sideResultsRaw : []), [sideResultsRaw]);
  const effectivePreviousSkinData = showAdvancedCharts ? previousSkinData : null;
  // Report LLM (Gemma): independent of segmentation capture mode ("basic" vs "advanced").
  const useOllamaForAi = aiGeneratedSummary !== 'no';
  const showRoutine = aiGeneratedRoutine !== 'no';

  useEffect(() => {
    if (showAdvancedCharts) return;
    if (previousSkinData !== null) setPreviousSkinData(null);
  }, [showAdvancedCharts, previousSkinData]);

  useEffect(() => {
    if (!showAdvancedCharts || !sessionId || previousSkinData != null) return;
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 4;

    const fetchPrevious = async () => {
      attempt += 1;
      try {
        const res = await getPreviousSkinAnalysis(sessionId, token);
        if (cancelled) return;
        if (res.success) {
          setPreviousSkinData(res.previous || null);
          return;
        }
      } catch (e) {
      }
      if (!cancelled && attempt < maxAttempts) {
        const delayMs = attempt <= 2 ? 1200 : 2200;
        setTimeout(fetchPrevious, delayMs);
      }
    };

    fetchPrevious();
    return () => { cancelled = true; };
  }, [sessionId, token, previousSkinData, showAdvancedCharts]);

  // If we navigated back from treatment/product pages, keep buttons actionable immediately.
  useEffect(() => {
    if (!cameBackWithRecommendations) return;
    setRecommendationGenerationState((prev) => ({
      ...prev,
      treatmentReady: true,
      productReady: true,
    }));
  }, [cameBackWithRecommendations]);

  // Persist recommendation readiness to flow state + in-memory cache so back/forward doesn't disable actions.
  useEffect(() => {
    if (!sessionId) return;
    const statusPayload = {
      treatmentReady: Boolean(recommendationGenerationState.treatmentReady),
      productReady: Boolean(recommendationGenerationState.productReady),
    };
    setSalonRecommendationStatusCache(sessionId, statusPayload);
    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, {
      ...current,
      sessionId,
      recommendationStatus: statusPayload,
    });
  }, [sessionId, recommendationGenerationState.treatmentReady, recommendationGenerationState.productReady]);

  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, {
      ...current,
      sessionId,
      treatmentCartItems,
      cartItems: productCartItems,
    });
  }, [sessionId, treatmentCartItems, productCartItems]);

  const handleSelectConcern = useCallback((concernName) => {
    const canonical = canonicalSkinConditionName(concernName) || concernName;
    setSelectedConcernName(canonical);
    setSelectedConcernView('front');
  }, []);

  // Back to top: show when either shell or main is scrolled (shell scrolls on mobile, main on desktop)
  useEffect(() => {
    const shell = scrollShellRef.current;
    const main = scrollMainRef.current;
    const onScroll = () => {
      const shellTop = shell?.scrollTop ?? 0;
      const mainTop = main?.scrollTop ?? 0;
      setShowBackToTop(shellTop > 300 || mainTop > 300);
    };
    onScroll();
    shell?.addEventListener('scroll', onScroll, { passive: true });
    main?.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      shell?.removeEventListener('scroll', onScroll);
      main?.removeEventListener('scroll', onScroll);
    };
  }, [isLoading, error]);
  const handleScrollToTop = useCallback(() => {
    scrollShellRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    scrollMainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const getStoredAqiData = () => {
    if (aqiDataFromState) return aqiDataFromState;
    if (sessionId) {
      const stored = getSalonAqiCache(sessionId);
      if (stored) return stored;
    }
    return null;
  };

  const [aqiData, setAqiData] = useState(getStoredAqiData());
  const [, setAqiLoading] = useState(!getStoredAqiData());
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showFallbackSummary, setShowFallbackSummary] = useState(!storedData?.summary);
  const [isTreatmentsLoading, setIsTreatmentsLoading] = useState(false);
  const [hasCompletedTreatmentsStep, setHasCompletedTreatmentsStep] = useState(false);
  const [, setHasCompletedProductsStep] = useState(false);
  const [, setHasCompletedDeepDiveStep] = useState(false);
  const [, setHasCompletedSummaryStep] = useState(Boolean(storedData?.summary));
  const hasGeneratedRef = useRef(!forceRegenerateForNewScan && hasUsableRoutineData(storedData));
  const hasFetchedAqiRef = useRef(!!getStoredAqiData());

  const getScoreTone = useCallback((rawScore) => {
    const score = Math.max(0, Math.min(100, Number(rawScore || 0)));
    if (score <= 39) return 'needs focused recovery';
    if (score <= 59) return 'shows visible imbalance';
    if (score <= 74) return 'is in a moderate state';
    if (score <= 89) return 'is in a good range';
    return 'is looking strong and balanced';
  }, []);

  const replaceScoreMentionsWithFriendlyWords = useCallback((text) => {
    if (!text || typeof text !== 'string') return text || '';

    const toTone = (numText) => getScoreTone(Number(numText));

    let out = text
      .replace(/\b(?:score\s*(?:is|:)?\s*)?(\d{1,3})\s*\/\s*100\b/gi, (_, n) => toTone(n))
      .replace(/\b(\d{1,3})\s*%\b/g, (_, n) => toTone(n));

    out = out.replace(/\s{2,}/g, ' ').trim();
    return out;
  }, [getScoreTone]);

  useEffect(() => {
    if (!sessionId || forceRegenerateForNewScan) return;

    const routinePayload = { summary, routine };
    if (!hasUsableRoutineData(routinePayload)) return;

    const macroPayload = macroAnalysis || photoInsight?.macro || null;
    const cachePayload = {
      ...routinePayload,
      skinAge,
      photoInsight,
      envTip,
      macroAnalysis: macroPayload,
      cacheVersion: ROUTINE_CACHE_VERSION,
    };

    setSalonRoutineCache(sessionId, cachePayload);

    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, {
      ...current,
      sessionId,
      macroAnalysis: macroPayload,
      routineData: { ...routinePayload, skinAge, photoInsight, envTip, macroAnalysis: macroPayload },
    });
  }, [sessionId, forceRegenerateForNewScan, summary, routine, skinAge, photoInsight, envTip, macroAnalysis]);

  // Skin age via light VL — skip if analysis page already passed visionInsight / routineData.
  useEffect(() => {
    if (isCompleteSkinAgeRecord(skinAge)) {
      setIsSkinAgeLoading(false);
      return;
    }
    const fromNav = applyVisionInsightPayload(effectiveState?.visionInsight, {
      buildSkinAgeExplanationFallback,
    });
    if (fromNav?.skinAgeRecord && isCompleteSkinAgeRecord(fromNav.skinAgeRecord)) {
      setSkinAge(fromNav.skinAgeRecord);
      if (fromNav.photoInsightRecord) setPhotoInsight(fromNav.photoInsightRecord);
      if (fromNav.envTipRecord) setEnvTip(fromNav.envTipRecord);
      setIsSkinAgeLoading(false);
      return;
    }
    const routineAge = effectiveState?.routineData?.skinAge;
    if (isCompleteSkinAgeRecord(routineAge)) {
      setSkinAge(routineAge);
      if (effectiveState?.routineData?.photoInsight) {
        setPhotoInsight(effectiveState.routineData.photoInsight);
      }
      if (effectiveState?.routineData?.envTip) {
        setEnvTip(effectiveState.routineData.envTip);
      }
      setIsSkinAgeLoading(false);
      return;
    }
    const image = capturedImageFromState || skinResultsFromState?.capturedImage;
    if (!image && !sessionId) {
      setIsSkinAgeLoading(false);
      return;
    }
    let cancelled = false;
    setIsSkinAgeLoading(true);
    const applyPayload = (payload) => {
      const parsed = applyVisionInsightPayload(payload, { buildSkinAgeExplanationFallback });
      if (!parsed) return false;
      setSkinAge(parsed.skinAgeRecord);
      if (parsed.photoInsightRecord) setPhotoInsight(parsed.photoInsightRecord);
      if (parsed.photoInsightRecord?.macro) {
        setMacroAnalysis(parsed.photoInsightRecord.macro);
      }
      if (parsed.envTipRecord) setEnvTip(parsed.envTipRecord);
      return true;
    };
    const run = async () => {
      try {
        if (sessionId) {
          const stored = await fetchStoredVisionInsight({
            sessionId,
            appSessionToken: token || null,
          });
          if (!cancelled && stored && applyPayload(stored)) {
            return;
          }
        }
        // Only regenerate VL when storage is empty (avoid duplicate Gemma jobs).
        if (!image) return;
        const data = await requestSkinAge({
          capturedImage: image,
          sessionId,
          appSessionToken: token || null,
        });
        if (!cancelled && data?.success) {
          applyPayload(data);
        }
      } catch (e) {
        // Non-blocking
      } finally {
        if (!cancelled) setIsSkinAgeLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [
    skinAge,
    capturedImageFromState,
    skinResultsFromState?.capturedImage,
    sessionId,
    token,
    effectiveState?.visionInsight,
    effectiveState?.routineData?.skinAge,
    effectiveState?.routineData?.photoInsight,
    effectiveState?.routineData?.envTip,
  ]);

  // Fetch environment tip when AQI arrives after the vision call.
  useEffect(() => {
    if (!useOllamaForAi || envTip || !photoInsight || !aqiData?.success) return;
    let cancelled = false;
    const run = async () => {
      try {
        const API_BASE_URL = getApiBaseUrl() || '';
        const res = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/skin-env-tip`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            ...(token ? { app_session_token: token } : {}),
            skin_type: photoInsight.skin_type,
            top_concerns: photoInsight.top_concerns || [],
            most_affected_region: photoInsight.most_affected_region,
            aqi_data: {
              aqi: aqiData.aqi,
              category: aqiData.category,
              location: aqiData.location,
              uv_index: aqiData.uv_index ?? null,
              uv_level: aqiData.uv_level,
              temperature: aqiData.temperature ?? null,
              humidity: aqiData.humidity ?? null,
              wind_speed: aqiData.wind_speed ?? null,
            },
          }),
          timeoutMs: 45000,
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data?.success && typeof data.env_tip === 'string' && isUsableEnvTip(data.env_tip)) {
          setEnvTip(data.env_tip.trim());
        }
      } catch (e) {
        // Non-blocking
      }
    };
    run();
    return () => { cancelled = true; };
  }, [useOllamaForAi, envTip, photoInsight, aqiData, sessionId, token]);

  // Single fetch for routine page controls (summary, products/treatments, concerns/conditions, advanced charts).
  useEffect(() => {
    if (!tokenReady) return undefined;
    let cancelled = false;

    const applySourceFields = (data) => {
      if (!data) return;
      setProductRecommendationSource(
        data?.product_recommendation_source === 'products_page' ? 'products_page' : 'database'
      );
      setProductsPageUrl(String(data?.products_page_url || '').trim());
      const urls = Array.isArray(data?.products_page_urls)
        ? data.products_page_urls.map((u) => String(u || '').trim()).filter(Boolean)
        : [];
      setProductsPageUrls(urls.length ? urls : (data?.products_page_url ? [String(data.products_page_url).trim()] : []));
      if (data?.organization_id != null && data.organization_id !== '') {
        setOrganizationId(String(data.organization_id));
      } else {
        const franchiseOrg = getFranchiseOrganizationId();
        if (franchiseOrg) setOrganizationId(String(franchiseOrg));
      }
    };

    if (!token) {
      const base = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';
      fetchWithTimeout(`${base}${SALON_API_PREFIX}/config/product-recommendation`, { timeoutMs: 20000 })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (cancelled) return;
          // No app token: still allow org catalog recommendations (backend uses DEFAULT_ORG / franchise org).
          setServiceSuggestionEnabled(true);
          if (data) {
            const productEnabled = parseFeatureToggle(data?.product_suggestion ?? data?.product ?? true);
            setProductSuggestionEnabled(productEnabled);
            applySourceFields(data);
          } else {
            setProductSuggestionEnabled(true);
          }
          setSuggestConfigLoaded(true);
        })
        .catch(() => {
          if (!cancelled) {
            setServiceSuggestionEnabled(true);
            setProductSuggestionEnabled(true);
            setSuggestConfigLoaded(true);
          }
        });
      return () => { cancelled = true; };
    }

    fetchSkinAnalysisBundle(token)
      .then((data) => {
        if (cancelled) return;
        const parsed = parseSegmentationPayload(data);

        setAiGeneratedSummary(parsed.aiGeneratedSummary);
        setSegmentationAiMode(parsed.aiMode);

        const aiRoutineValue = toYesNoValue(data?.ai_generated_routine);
        setAiGeneratedRoutine(aiRoutineValue);

        // Empty string must not disable treatments (`??` does not treat "" as missing).
        const rawTreatmentFlag = data?.service_suggestion ?? data?.treatment;
        const treatmentEnabled =
          rawTreatmentFlag == null || String(rawTreatmentFlag).trim() === ''
            ? true
            : parseFeatureToggle(rawTreatmentFlag);
        const rawProductFlag = data?.product_suggestion ?? data?.product ?? parsed.productSuggestion;
        const productEnabled =
          rawProductFlag == null || String(rawProductFlag).trim() === ''
            ? true
            : parseFeatureToggle(rawProductFlag);
        setServiceSuggestionEnabled(treatmentEnabled);
        setProductSuggestionEnabled(productEnabled);
        setAddToCartEnabled(parsed.addToCart);
        setProductLinkEnabled(parsed.productLink);
        applySourceFields(data);

        setShowAdvancedCharts(parsed.advancedCharts);
        setShowSkinConcerns(parsed.showConcerns);
        setShowSkinConditions(parsed.showConditions);
        setSegmentationAllowedNames(parsed.allowedNames);
        setSuggestConfigLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setAiGeneratedSummary(null);
          setSegmentationAiMode(null);
          setAiGeneratedRoutine(null);
          setShowAdvancedCharts(true);
          setShowSkinConcerns(true);
          setShowSkinConditions(true);
          setSegmentationAllowedNames(null);
          setServiceSuggestionEnabled(true);
          setProductSuggestionEnabled(true);
          setAddToCartEnabled(false);
          setProductLinkEnabled(false);
          setSuggestConfigLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [token, tokenReady]);

  const recommendationRequirements = useMemo(() => {
    return {
      treatmentRequired: serviceSuggestionEnabled,
      productRequired: productSuggestionEnabled,
    };
  }, [serviceSuggestionEnabled, productSuggestionEnabled]);

  const buildRecommendationPayload = useCallback(() => {
    const allDetections = skinResultsFromState?.detections || [];
    const filteredDetections =
      visibleConditionNames != null
        ? allDetections.filter((d) => {
          const name = d.class_name || d.className;
          return name && visibleConditionNames.includes(name);
        })
        : allDetections;
    const detectionsForRecommendation = filteredDetections.length > 0 ? filteredDetections : allDetections;

    const skinScore = skinResultsFromState?.skinScore || {};
    const conditionScores = buildRecommendationPayloadScores(skinScore, detectionsForRecommendation);
    const detectedFromDetections = detectionsForRecommendation
      .map((d) => d.class_name || d.className || d.name)
      .filter(Boolean);
    const detectedConditions = buildRecommendableConditions(
      detectedFromDetections,
      conditionScores,
      detectionsForRecommendation,
    );

    const resolvedOrgId =
      organizationId
      || getFranchiseOrganizationId()
      || null;
    const resolvedFranchiseId = getFranchiseId() || null;
    const genderForApi = resolvePresentationGuestGenderForApi(userDataFromState?.gender);

    return {
      session_id: sessionId,
      app_session_token: token,
      final_skin_score: skinScore?.final_skin_score || skinScore?.score || 0,
      skin_grade: skinScore?.grade || 'Transitioning',
      condition_scores: conditionScores,
      detected_conditions: detectedConditions,
      detections: detectionsForRecommendation,
      skin_label_scores: skinScore.skin_label_scores || {},
      attribute_scores: skinScore.attribute_scores || {},
      user_age: userDataFromState?.age,
      user_skin_type: userDataFromState?.skin_type || userDataFromState?.skinType,
      product_recommendation_source: productRecommendationSource,
      ...(resolvedOrgId ? { organization_id: resolvedOrgId } : {}),
      ...(resolvedFranchiseId ? { franchise_id: resolvedFranchiseId } : {}),
      ...(genderForApi ? { gender: genderForApi } : {}),
      ...(productRecommendationSource === 'products_page' && (productsPageUrls.length || productsPageUrl)
        ? {
            products_page_url: productsPageUrls[0] || productsPageUrl,
            products_page_urls: productsPageUrls.length ? productsPageUrls : [productsPageUrl],
          }
        : {}),
    };
  }, [sessionId, token, skinResultsFromState, visibleConditionNames, userDataFromState, organizationId, productRecommendationSource, productsPageUrl, productsPageUrls]);

  const fetchStoredRecommendationsStatus = useCallback(async () => {
    if (!sessionId) return { treatmentReady: false, productReady: false };
    const API_BASE_URL = getApiBaseUrl() || '';
    const status = { treatmentReady: false, productReady: false };

    if (recommendationRequirements.productRequired) {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-products/${encodeURIComponent(sessionId)}?app_session_token=${encodeURIComponent(token || '')}${getFranchiseId() ? `&franchise_id=${encodeURIComponent(String(getFranchiseId()))}` : ''}`,
          { timeoutMs: 20000 }
        );
        const data = await response.json().catch(() => ({}));
        if (response.ok && data?.success && hasRecommendedProductsPayload(data?.recommended_products)) {
          status.productReady = true;
        }
      } catch (e) {
      }
    } else {
      status.productReady = true;
    }

    if (recommendationRequirements.treatmentRequired) {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-treatments/${encodeURIComponent(sessionId)}?app_session_token=${encodeURIComponent(token || '')}`,
          { timeoutMs: 20000 }
        );
        const data = await response.json().catch(() => ({}));
        if (response.ok && data?.success && hasRecommendedTreatmentsPayload(data?.recommended_treatments)) {
          status.treatmentReady = true;
        }
      } catch (e) {
      }
    } else {
      status.treatmentReady = true;
    }

    return status;
  }, [recommendationRequirements.productRequired, recommendationRequirements.treatmentRequired, sessionId, token]);

  const fetchStoredTreatments = useCallback(async () => {
    if (!sessionId) {
      setRecommendedTreatments([]);
      return [];
    }
    const API_BASE_URL = getApiBaseUrl() || '';
    try {
      const storedParams = new URLSearchParams();
      if (token) storedParams.set('app_session_token', token);
      const orgForStored = organizationId || getFranchiseOrganizationId() || null;
      if (orgForStored) storedParams.set('organization_id', orgForStored);
      const fid = getFranchiseId();
      if (fid) storedParams.set('franchise_id', String(fid));
      const storedQuery = storedParams.toString() ? `?${storedParams.toString()}` : '';
      const response = await fetchWithTimeout(
        `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-treatments/${encodeURIComponent(sessionId)}${storedQuery}`,
        { timeoutMs: 20000 }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.success && hasRecommendedTreatmentsPayload(data?.recommended_treatments)) {
        setRecommendedTreatments(data.recommended_treatments);
        return data.recommended_treatments;
      }
      // Do not clear an in-memory list here — caller may keep a successful recommend payload.
      return [];
    } catch (e) {
      return [];
    }
  }, [sessionId, token, organizationId]);

  const fetchStoredProducts = useCallback(async () => {
    if (!sessionId) {
      setRecommendedProducts({ morning: [], evening: [] });
      return { morning: [], evening: [] };
    }
    const API_BASE_URL = getApiBaseUrl() || '';
    try {
      const storedParams = new URLSearchParams();
      if (token) storedParams.set('app_session_token', token);
      if (organizationId) storedParams.set('organization_id', organizationId);
      if (productRecommendationSource === 'products_page') storedParams.set('refresh_live', '1');
      const storedQuery = storedParams.toString() ? `?${storedParams.toString()}` : '';
      const response = await fetchWithTimeout(
        `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-products/${encodeURIComponent(sessionId)}${storedQuery}`,
        { timeoutMs: 20000 }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.success && hasRecommendedProductsPayload(data?.recommended_products)) {
        const morning = Array.isArray(data?.recommended_products?.morning) ? data.recommended_products.morning : [];
        const evening = Array.isArray(data?.recommended_products?.evening) ? data.recommended_products.evening : [];
        const productsOut = { morning, evening };
        setRecommendedProducts(productsOut);
        return productsOut;
      } else {
        setRecommendedProducts({ morning: [], evening: [] });
        return { morning: [], evening: [] };
      }
    } catch (e) {
      setRecommendedProducts({ morning: [], evening: [] });
      return { morning: [], evening: [] };
    }
  }, [sessionId, token, organizationId, productRecommendationSource]);

  const generateTreatmentsDirectly = useCallback(async () => {
    if (!sessionId) return [];

    const API_BASE_URL = getApiBaseUrl() || '';
    const payload = {
      ...buildRecommendationPayload(),
      treatment_suggest_limit: 8,
    };

    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/recommend-treatments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: 60000,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success || !hasRecommendedTreatmentsPayload(data?.recommended_treatments)) {
        return [];
      }
      const treatmentsOut = Array.isArray(data?.recommended_treatments) ? data.recommended_treatments : [];
      setRecommendedTreatments(treatmentsOut);
      setRecommendationGenerationState((prev) => ({
        ...prev,
        treatmentReady: true,
        error: null,
      }));
      return treatmentsOut;
    } catch {
      return [];
    }
  }, [buildRecommendationPayload, sessionId]);

  const hydrateTreatmentsList = useCallback(async () => {
    if (!serviceSuggestionEnabled) {
      setHasCompletedTreatmentsStep(true);
      return [];
    }
    setIsTreatmentsLoading(true);
    try {
      let treatmentsNow = await fetchStoredTreatments();
      if (!hasRecommendedTreatmentsPayload(treatmentsNow)) {
        treatmentsNow = await generateTreatmentsDirectly();
        if (hasRecommendedTreatmentsPayload(treatmentsNow)) {
          const stored = await fetchStoredTreatments();
          if (hasRecommendedTreatmentsPayload(stored)) {
            treatmentsNow = stored;
          } else {
            setRecommendedTreatments(treatmentsNow);
          }
        }
      }
      setHasCompletedTreatmentsStep(true);
      setRecommendationGenerationState((prev) => ({
        ...prev,
        treatmentReady: hasRecommendedTreatmentsPayload(treatmentsNow) || prev.treatmentReady,
      }));
      return treatmentsNow;
    } finally {
      setIsTreatmentsLoading(false);
    }
  }, [serviceSuggestionEnabled, fetchStoredTreatments, generateTreatmentsDirectly]);

  const generateProductsDirectly = useCallback(async () => {
    if (!sessionId) return { morning: [], evening: [] };

    const API_BASE_URL = getApiBaseUrl() || '';
    const payload = buildRecommendationPayload();

    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/recommend-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: 60000,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success || !hasRecommendedProductsPayload(data?.recommended_products)) {
        return { morning: [], evening: [] };
      }
      const morning = Array.isArray(data?.recommended_products?.morning) ? data.recommended_products.morning : [];
      const evening = Array.isArray(data?.recommended_products?.evening) ? data.recommended_products.evening : [];
      const productsOut = { morning, evening };
      setRecommendedProducts(productsOut);
      setRecommendationGenerationState((prev) => ({
        ...prev,
        productReady: true,
        error: null,
      }));
      return productsOut;
    } catch {
      return { morning: [], evening: [] };
    }
  }, [buildRecommendationPayload, sessionId]);

  const generateDeepDiveDirectly = useCallback(async () => {
    if (!sessionId || !skinResultsFromState) return {};

    const API_BASE_URL = getApiBaseUrl() || '';
    const allDetections = skinResultsFromState?.detections || [];
    const detectionsForDeepDive =
      visibleConditionNames != null
        ? allDetections.filter((d) => {
          const name = d.class_name || d.className;
          return name && visibleConditionNames.includes(name);
        })
        : allDetections;

    const rawConditions = skinResultsFromState?.skinConditions || null;
    const leanSkinConditions = rawConditions && typeof rawConditions === 'object'
      ? Object.fromEntries(
        Object.entries(rawConditions).map(([k, v]) => {
          if (!v || typeof v !== 'object') return [k, v];
          const { mask, ...rest } = v;
          return [k, rest];
        })
      )
      : rawConditions;

    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/generate-analysis-deep-dive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          detections: detectionsForDeepDive,
          skin_score: skinResultsFromState?.skinScore || null,
          previous_skin_data: effectivePreviousSkinData,
          skin_conditions: leanSkinConditions,
          user_data: {
            ...(userDataFromState || {}),
            session_id: sessionId,
          },
          aqi_data: aqiData && aqiData.success ? {
            aqi: aqiData.aqi,
            category: aqiData.category,
            location: aqiData.location,
            coordinates: aqiData.coordinates,
            latitude: aqiData.latitude,
            longitude: aqiData.longitude,
            uv_index: aqiData.uv_index !== undefined && aqiData.uv_index !== null ? aqiData.uv_index : null,
            uv_level: aqiData.uv_level,
            temperature: aqiData.temperature !== undefined && aqiData.temperature !== null ? aqiData.temperature : null,
            humidity: aqiData.humidity !== undefined && aqiData.humidity !== null ? aqiData.humidity : null,
            wind_speed: aqiData.wind_speed !== undefined && aqiData.wind_speed !== null ? aqiData.wind_speed : null,
          } : null,
          session_id: sessionId,
          app_session_token: token,
          use_ollama_for_descriptions: useOllamaForAi,
        }),
        timeoutMs: 60000,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success || !data?.deep_dive || typeof data.deep_dive !== 'object') {
        return {};
      }
      return normalizeDescriptionMap(data.deep_dive) || {};
    } catch {
      return {};
    }
  }, [aqiData, effectivePreviousSkinData, sessionId, skinResultsFromState, token, userDataFromState, useOllamaForAi, visibleConditionNames]);

  // Rehydrate recommendation readiness from stored backend records.
  // This keeps button gating correct after refresh/navigation when routine data is cached.
  useEffect(() => {
    if (!suggestConfigLoaded || !sessionId) return;
    let cancelled = false;

    const refreshStatus = async () => {
      const status = await fetchStoredRecommendationsStatus();
      if (cancelled) return;
      setRecommendationGenerationState((prev) => ({
        ...prev,
        // Never downgrade once ready; avoids transient API/state gaps disabling buttons after back/forward.
        treatmentReady: prev.treatmentReady || status.treatmentReady,
        productReady: prev.productReady || status.productReady,
      }));
    };

    refreshStatus();
    return () => {
      cancelled = true;
    };
  }, [fetchStoredRecommendationsStatus, sessionId, suggestConfigLoaded]);

  // Hide Summary tab only when session config ai_generated_summary === "no"; flag only controls Ollama vs default content
  const showSummaryAndInsights = aiGeneratedSummary !== 'no';

  // Concerns and conditions are controlled by segmentation payload fetched above.
  const [segmentationAllowedNames, setSegmentationAllowedNames] = useState(null);

  // Helper function to get UV Index color based on value
  const getUvIndexColor = (uvIndex) => {
    if (uvIndex <= 2) return '#00E400'; // Low - Green
    if (uvIndex <= 5) return '#FFFF00'; // Moderate - Yellow
    if (uvIndex <= 7) return '#FF7E00'; // High - Orange
    if (uvIndex <= 10) return '#FF0000'; // Very High - Red
    return '#8F3F97'; // Extreme - Purple
  };

  const getUvDescription = (uvIndex) => {
    if (uvIndex == null || uvIndex === undefined) return 'N/A';
    const n = Number(uvIndex);
    if (n <= 2) return 'Low';
    if (n <= 5) return 'Moderate';
    if (n <= 7) return 'High';
    if (n <= 10) return 'Very High';
    return 'Extreme';
  };

  // Fetch AQI data (only once per session) - Skip if already in state
  useEffect(() => {
    // Skip if AQI data already exists in state or has been fetched
    if (aqiDataFromState && aqiDataFromState.success) {
      setAqiData(aqiDataFromState);
      hasFetchedAqiRef.current = true;
      setAqiLoading(false);
      return;
    }

    if (hasFetchedAqiRef.current || aqiData) {
      setAqiLoading(false);
      return;
    }

    const fetchAQI = async () => {
      // Only fetch if geolocation is supported
      if (!navigator.geolocation) {
        setAqiLoading(false);
        return;
      }

      setAqiLoading(true);
      const API_BASE_URL = getApiBaseUrl() || '';

      try {
        // Get user's location
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          );
        });

        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        // Fetch AQI data from backend
        const response = await fetchWithTimeout(
          `${API_BASE_URL}${SALON_API_PREFIX}/aqi?latitude=${latitude}&longitude=${longitude}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
            ,
            timeoutMs: 20000,
          }
        );

        const data = await response.json().catch(() => ({}));

        if (response.ok && data.success) {
          setAqiData(data);
          hasFetchedAqiRef.current = true;
          // Store AQI data in localStorage with session ID
          if (sessionId) {
            setSalonAqiCache(sessionId, data);
          }
          // Save AQI data (including temperature) to database
          if (sessionId && data.success) {
            try {
              await saveAqiUpdate(sessionId, data, token || null);
            } catch (error) {
              console.warn('Failed to save AQI data to database:', error);
            }
          }
        }
      } catch (error) {
        // Silently fail - AQI is optional
      } finally {
        setAqiLoading(false);
      }
    };

    fetchAQI();
  }, [sessionId, aqiData, aqiDataFromState, token]);

  useEffect(() => {
    if (!serviceSuggestionEnabled) {
      setHasCompletedTreatmentsStep(true);
    }
  }, [serviceSuggestionEnabled]);

  useEffect(() => {
    if (!productSuggestionEnabled) {
      setHasCompletedProductsStep(true);
    }
  }, [productSuggestionEnabled]);

  useEffect(() => {
    if (!showSummaryAndInsights) {
      setHasCompletedSummaryStep(true);
    }
  }, [showSummaryAndInsights]);

  // Treatments hydrate separately so a one-shot populate ref cannot leave the section empty forever.
  useEffect(() => {
    if (!tokenReady || !suggestConfigLoaded) return;
    if (!serviceSuggestionEnabled) {
      setHasCompletedTreatmentsStep(true);
      return;
    }
    const hydrateKey = `${sessionId || ''}:${organizationId || ''}`;
    if (treatmentsHydrateSessionRef.current !== hydrateKey) {
      treatmentsHydrateSessionRef.current = hydrateKey;
      treatmentsHydrateStartedRef.current = false;
    }
    if (recommendedTreatments.length > 0) {
      setHasCompletedTreatmentsStep(true);
      return;
    }
    if (treatmentsHydrateStartedRef.current) return;
    treatmentsHydrateStartedRef.current = true;
    let active = true;
    void (async () => {
      let list = await hydrateTreatmentsList();
      if (!active) return;
      if (!hasRecommendedTreatmentsPayload(list)) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (!active) return;
        list = await hydrateTreatmentsList();
      }
      if (!active) return;
      if (!hasRecommendedTreatmentsPayload(list)) {
        treatmentsHydrateStartedRef.current = false;
      }
    })();
    return () => {
      active = false;
      treatmentsHydrateStartedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid retrigger loops from payload callback identity
  }, [
    tokenReady,
    suggestConfigLoaded,
    serviceSuggestionEnabled,
    recommendedTreatments.length,
    organizationId,
    sessionId,
    userDataFromState?.gender,
  ]);

  // Pre-select treatments matching check-in / service-session selections.
  useEffect(() => {
    if (servicePreselectDoneRef.current) return;
    if (!Array.isArray(recommendedTreatments) || !recommendedTreatments.length) return;
    const names = readSelectedServiceNames();
    if (!names.length) {
      servicePreselectDoneRef.current = true;
      return;
    }

    const next = { ...(treatmentCartItems || {}) };
    let added = 0;
    recommendedTreatments.forEach((t, i) => {
      const label = String(t?.treatment || t?.name || t?.service_name || '')
        .trim()
        .toLowerCase();
      if (!label) return;
      const matched = names.some(
        (n) => label.includes(n) || n.includes(label) || label === n,
      );
      if (!matched) return;
      const key = treatmentRowCartKey(t, i);
      if (next[key]) return;
      next[key] = { treatment: t, quantity: 1 };
      added += 1;
    });
    servicePreselectDoneRef.current = true;
    if (added > 0) {
      setTreatmentCartItems(next);
    }
  }, [recommendedTreatments, treatmentCartItems]);

  const handleRetryTreatments = () => {
    treatmentsHydrateStartedRef.current = false;
    void hydrateTreatmentsList().then((list) => {
      if (!hasRecommendedTreatmentsPayload(list)) {
        treatmentsHydrateStartedRef.current = false;
      }
    });
  };

  // Ordered populate flow:
  // 1) concern preview default (already immediate)
  // 2) recommended treatments (dedicated effect above)
  // 3) recommended products
  // 4) analysis deep dive
  // 5) AI summary
  useEffect(() => {
    if (!tokenReady || !suggestConfigLoaded) {
      return;
    }
    if (recommendGenerationStartedRef.current) {
      return;
    }

    const loadProducts = async () => {
      if (!productSuggestionEnabled) return { morning: [], evening: [] };
      setIsProductsLoading(true);
      try {
        // products_page: always re-fetch live catalog (stored IDs are analytics-only snapshots)
        if (productRecommendationSource === 'products_page') {
          let productsNow = await generateProductsDirectly();
          if (hasRecommendedProductsPayload(productsNow)) {
            return productsNow;
          }
          return await fetchStoredProducts();
        }
        let productsNow = await fetchStoredProducts();
        if (!hasRecommendedProductsPayload(productsNow)) {
          productsNow = await generateProductsDirectly();
          if (hasRecommendedProductsPayload(productsNow)) {
            productsNow = await fetchStoredProducts();
          }
        }
        return productsNow;
      } finally {
        setIsProductsLoading(false);
      }
    };

    const hydrateRecommendations = async () => {
      const productsNow = await loadProducts();

      const productOk = !productSuggestionEnabled || hasRecommendedProductsPayload(productsNow);
      const recErrors = [];
      if (productSuggestionEnabled && !productOk) {
        recErrors.push('Product recommendations could not be generated.');
      }

      setRecommendationGenerationState((prev) => ({
        ...prev,
        productReady: productOk || prev.productReady,
        error: recErrors.length ? recErrors.join(' ') : null,
      }));
      if (recErrors.length) {
        setError(recErrors.join(' '));
      }
      setHasCompletedProductsStep(true);
      if (sessionId) {
        const morning = Array.isArray(productsNow?.morning) ? productsNow.morning : [];
        const evening = Array.isArray(productsNow?.evening) ? productsNow.evening : [];
        const hasProducts = productOk && hasRecommendedProductsPayload(productsNow);
        void syncAnalysisRecommendedProductsReady('skin_analysis', {
          salonReference: sessionId,
          productCount: hasProducts ? morning.length + evening.length : 0,
        });
      }
    };

    // Cached routine previously skipped this whole effect and never loaded treatments.
    if (hasGeneratedRef.current) {
      recommendGenerationStartedRef.current = true;
      void (async () => {
        setError(null);
        setRecommendationGenerationState((prev) => ({ ...prev, error: null }));
        await hydrateRecommendations();
        setIsLoading(false);
      })();
      return;
    }

    const initializeOrderedPopulation = async () => {
      if (!skinResultsFromState) {
        setError('Missing required data');
        setIsLoading(false);
        return;
      }

      recommendGenerationStartedRef.current = true;
      setError(null);
      setRecommendationGenerationState((prev) => ({ ...prev, error: null }));

      const runConditionDescriptions = async () => {
        if (!useOllamaForAi || !sessionId) return;
        setIsLoadingConditionDescriptions(true);
        try {
          const storedDescriptions = normalizeDescriptionMap(await fetchStoredConditionDescriptions({
            sessionId,
            appSessionToken: token || null,
            generateIfMissing: false,
            previousSkinData: effectivePreviousSkinData,
          }));
          if (storedDescriptions && Object.keys(storedDescriptions).length > 0) {
            setAiConditionDescriptions((prev) => {
              const merged = normalizeDescriptionMap({ ...(prev || {}), ...storedDescriptions }) || {};
              const current = getSkinFlowState(sessionId) || {};
              saveSkinFlowState(sessionId, {
                ...current,
                sessionId,
                aiConditionDescriptions: merged,
              });
              return merged;
            });
            return;
          }
          const deepDiveDescriptions = await generateDeepDiveDirectly();
          if (deepDiveDescriptions && Object.keys(deepDiveDescriptions).length > 0) {
            setAiConditionDescriptions((prev) => {
              const merged = normalizeDescriptionMap({ ...(prev || {}), ...deepDiveDescriptions }) || {};
              const current = getSkinFlowState(sessionId) || {};
              saveSkinFlowState(sessionId, {
                ...current,
                sessionId,
                aiConditionDescriptions: merged,
              });
              return merged;
            });
          }
        } catch {
          // Non-blocking
        } finally {
          setIsLoadingConditionDescriptions(false);
          setHasCompletedDeepDiveStep(true);
        }
      };

      const runSummaryAndRoutine = async () => {
        if (!showSummaryAndInsights || (summary?.trim() && !forceRegenerateForNewScan)) {
          return;
        }
        setSummaryLoading(true);
        try {
          const API_BASE_URL = getApiBaseUrl() || '';
          const useOllamaSummary = useOllamaForAi;
          const useOllamaRoutine = showRoutine;
          const allDetections = skinResultsFromState?.detections || [];
          const detectionsForSummary =
            visibleConditionNames != null
              ? allDetections.filter((d) => {
                const name = d.class_name || d.className;
                return name && visibleConditionNames.includes(name);
              })
              : allDetections;
          const rawConditions = skinResultsFromState?.skinConditions || null;
          const leanSkinConditions = rawConditions && typeof rawConditions === 'object'
            ? Object.fromEntries(
              Object.entries(rawConditions).map(([k, v]) => {
                if (!v || typeof v !== 'object') return [k, v];
                const { mask, ...rest } = v;
                return [k, rest];
              })
            )
            : rawConditions;

          const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/generate-routine`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              detections: detectionsForSummary,
              skin_score: skinResultsFromState?.skinScore || null,
              previous_skin_data: effectivePreviousSkinData,
              skin_conditions: leanSkinConditions,
              user_data: {
                ...(userDataFromState || {}),
                session_id: sessionId,
              },
              aqi_data: aqiData && aqiData.success ? {
                aqi: aqiData.aqi,
                category: aqiData.category,
                location: aqiData.location,
                coordinates: aqiData.coordinates,
                latitude: aqiData.latitude,
                longitude: aqiData.longitude,
                uv_index: aqiData.uv_index !== undefined && aqiData.uv_index !== null ? aqiData.uv_index : null,
                uv_level: aqiData.uv_level,
                temperature: aqiData.temperature !== undefined && aqiData.temperature !== null ? aqiData.temperature : null,
                humidity: aqiData.humidity !== undefined && aqiData.humidity !== null ? aqiData.humidity : null,
                wind_speed: aqiData.wind_speed !== undefined && aqiData.wind_speed !== null ? aqiData.wind_speed : null,
              } : null,
              session_id: sessionId,
              app_session_token: token,
              ai_generated_summary: useOllamaSummary,
              use_ollama_for_summary: useOllamaSummary,
              use_ollama_for_routine: useOllamaRoutine,
              skip_product_recommendations: true,
              skip_vision_insight: true,
              force_regenerate: forceRegenerateForNewScan,
              force_regenerate_vision: false,
              captured_image: null,
            }),
            timeoutMs: 180000,
          });

          const data = await response.json().catch(() => ({}));
          if (data?.success) {
            const visionApplied = applyVisionInsightPayload(data, { buildSkinAgeExplanationFallback });
            if (visionApplied) {
              setSkinAge(visionApplied.skinAgeRecord);
              if (visionApplied.photoInsightRecord) setPhotoInsight(visionApplied.photoInsightRecord);
              if (visionApplied.photoInsightRecord?.macro) {
                setMacroAnalysis(visionApplied.photoInsightRecord.macro);
              }
              if (visionApplied.envTipRecord) setEnvTip(visionApplied.envTipRecord);
            }
            setIsSkinAgeLoading(false);
          }
          if (response.ok && data?.success && typeof data?.summary === 'string' && data.summary.trim()) {
            setSummary(data.summary);
            setShowFallbackSummary(false);
          } else {
            setShowFallbackSummary(true);
          }
          const tipsFromApi = Array.isArray(data?.tips)
            ? data.tips
            : (Array.isArray(data?.routine?.tips) ? data.routine.tips : null);
          if (tipsFromApi?.length) {
            setReportTips(
              tipsFromApi
                .map((t) => sanitizeTipText(String(t).trim()))
                .filter((t) => t && t.length > 8)
                .slice(0, 6),
            );
          }
          if (showRoutine && data?.routine && typeof data.routine === 'object') {
            const morning = Array.isArray(data.routine.morning) ? data.routine.morning : [];
            const evening = Array.isArray(data.routine.evening) ? data.routine.evening : [];
            if (morning.length || evening.length) {
              setRoutine({
                morning,
                evening,
                tips: tipsFromApi || data.routine.tips || [],
              });
            }
          }
        } catch {
          setShowFallbackSummary(true);
        } finally {
          setSummaryLoading(false);
          if (forceRegenerateForNewScan) {
            setIsSkinAgeLoading(false);
          }
        }
      };

      await runSummaryAndRoutine();
      setHasCompletedSummaryStep(true);
      hasGeneratedRef.current = true;
      setIsLoading(false);

      void Promise.all([
        hydrateRecommendations(),
        serviceSuggestionEnabled ? hydrateTreatmentsList() : Promise.resolve(),
      ]);
      void runConditionDescriptions();
    };

    initializeOrderedPopulation();
  }, [
    aqiData,
    useOllamaForAi,
    capturedImageFromState,
    fetchStoredProducts,
    forceRegenerateForNewScan,
    generateDeepDiveDirectly,
    generateProductsDirectly,
    hydrateTreatmentsList,
    serviceSuggestionEnabled,
    effectivePreviousSkinData,
    showAdvancedCharts,
    productSuggestionEnabled,
    productRecommendationSource,
    sessionId,
    showSummaryAndInsights,
    showRoutine,
    skinResultsFromState,
    suggestConfigLoaded,
    summary,
    token,
    tokenReady,
    userDataFromState,
    visibleConditionNames,
  ]);

  const handleCloseUspPopup = () => setUspPopup({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });

  const isAiContentLoading = summaryLoading;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (uspPopup.open && !e.target.closest('.product-usp-popup') && !e.target.closest('.product-usp')) handleCloseUspPopup();
    };
    if (uspPopup.open) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [uspPopup.open]);

  const handleBack = () => {
    navigate(pathWithSessionId('/skin-analysis', sessionId), {
      state: {
        capturedImage: capturedImageFromState,
        userData: userDataFromState,
        sessionId: sessionId,
        skinResults: skinResultsFromState,
        aqiData: aqiData,
        aiConditionDescriptions,
        ...(token && { token }),
      }
    });
  };

  const handleHome = async () => {
    await goSalonHubForNewUser(navigate, {
      sessionId,
      token: token || null,
    });
  };

  const handleAddTreatmentToCart = (treatment, quantity, cartKey) => {
    setTreatmentCartItems((prev) => {
      if (quantity === 0) {
        const next = { ...prev };
        delete next[cartKey];
        return next;
      }
      return { ...prev, [cartKey]: { treatment, quantity } };
    });
  };

  const handleViewProduct = useCallback((product, productUrl) => {
    const href = String(productUrl || product?.product_link || product?.link || product?.checkout_url || '').trim();
    const numericProductId = Number(product?.id ?? product?.product_id);
    const productId = Number.isFinite(numericProductId) ? numericProductId : null;
    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    if (baseUrl && sessionId && productId) {
      fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/record-viewed-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skin_id: sessionId,
          session_id: sessionId,
          product_id: productId,
          ...(token && { app_session_token: token }),
        }),
        timeoutMs: 10000,
      }).catch(() => {});
    }
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }, [API_BASE_URL, sessionId, token]);

  const handleAddProductToCart = (product, quantity, productId) => {
    setProductCartItems((prev) => {
      if (quantity === 0) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return {
        ...prev,
        [productId]: {
          product,
          quantity,
        },
      };
    });
  };

  const getTotalTreatmentCartItems = () =>
    Object.values(treatmentCartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getTotalProductCartItems = () =>
    Object.values(productCartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const handleOpenHomeConfirmPopup = () => setShowHomeConfirmPopup(true);
  const handleCloseHomeConfirmPopup = () => setShowHomeConfirmPopup(false);
  const handleConfirmGoHome = () => {
    setShowHomeConfirmPopup(false);
    goSalonHubForNewUser(navigate, {
      sessionId,
      token: token || null,
    });
  };
  const handleTreatmentCartUpdate = (updatedMap) => setTreatmentCartItems(updatedMap || {});
  const handleProductCartUpdate = (updatedMap) => setProductCartItems(updatedMap || {});

  const handleConfirmServices = async () => {
    if (confirmBusy || getTotalTreatmentCartItems() <= 0) return;
    const ok = await modernConfirm(
      `Confirm ${getTotalTreatmentCartItems()} selected service(s) for this visit? They will be started for the stylist desk.`,
      { title: 'Confirm services', confirmLabel: 'Confirm', cancelLabel: 'Cancel' },
    );
    if (!ok) return;
    setConfirmBusy(true);
    setConfirmNotice('');
    try {
      const lines = treatmentCartToPendingServices(treatmentCartItems, 'skin_analysis');
      await confirmAnalysisServicesAndStart(lines, 'skin_analysis');
      setConfirmNotice('Services confirmed and started for this visit.');
    } catch (err) {
      setConfirmNotice(err?.message || 'Could not confirm services. Please try again.');
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleConfirmProducts = async () => {
    if (confirmBusy || getTotalProductCartItems() <= 0) return;
    const ok = await modernConfirm(
      `Confirm ${getTotalProductCartItems()} selected product(s) for the front desk?`,
      { title: 'Confirm products', confirmLabel: 'Confirm', cancelLabel: 'Cancel' },
    );
    if (!ok) return;
    setConfirmBusy(true);
    setConfirmNotice('');
    try {
      await persistProductCart(getApiBaseUrl() || '', sessionId, token, productCartItems);
      await syncAnalysisProductCartToCheckin(productCartItems, 'skin_analysis');
      setConfirmNotice('Products confirmed for the front desk.');
    } catch (err) {
      setConfirmNotice(err?.message || 'Could not confirm products. Please try again.');
    } finally {
      setConfirmBusy(false);
    }
  };

  const saveProductCartToBackend = useCallback((items) => {
    const baseUrl = getApiBaseUrl() || '';
    if (!baseUrl || !sessionId) return;
    persistProductCart(baseUrl, sessionId, token, items);
  }, [sessionId, token]);

  const saveTreatmentCartToBackend = useCallback((items) => {
    const baseUrl = getApiBaseUrl() || '';
    if (!baseUrl || !sessionId) return;
    persistTreatmentCart(baseUrl, sessionId, token, items);
  }, [sessionId, token]);

  useEffect(() => {
    saveProductCartToBackend(productCartItems);
    void syncAnalysisProductCartToCheckin(productCartItems, 'skin_analysis');
  }, [productCartItems, saveProductCartToBackend]);

  useEffect(() => {
    saveTreatmentCartToBackend(treatmentCartItems);
  }, [treatmentCartItems, saveTreatmentCartToBackend]);

  useEffect(() => {
    const lines = treatmentCartToPendingServices(treatmentCartItems, 'skin_analysis');
    scheduleSyncAnalysisPendingServices(lines, 'skin_analysis');
  }, [treatmentCartItems]);

  const normalizeWhyRecommendedText = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text
      .replace(/\(\s*score\s*:\s*(\d{1,3})(?:\s*\/\s*100)?\s*\)/gi, '')
      .replace(/\bscore\s*[:=]?\s*(\d{1,3})(?:\s*\/\s*100|\s*%)?\b/gi, '')
      .trim();
  };

  const getRecommendationLine = (item) => {
    const conditionsToShow = item?.suggested_for_conditions?.length
      ? item.suggested_for_conditions
      : item?.conditions || [];
    if (!conditionsToShow.length) {
      return item?.suggested_for_wording ? normalizeWhyRecommendedText(item.suggested_for_wording) : '';
    }

    const parts = conditionsToShow.map((c) => c);
    return 'Recommended for ' + parts.join(', ');
  };

  const buildTreatmentDetailSections = (item) => {
    const sections = [];
    const push = (label, value) => {
      if (value == null) return;
      if (Array.isArray(value)) {
        const list = value.map((v) => String(v || '').trim()).filter(Boolean);
        if (!list.length) return;
        sections.push({ label, value: list.join(', '), list });
        return;
      }
      const text = String(value).trim();
      if (!text) return;
      sections.push({ label, value: text });
    };
    push('About', item?.about_treatment || item?.description);
    push('Key benefit', item?.major_usp);
    push('Kit / Product', item?.kit);
    push('Core ingredients', item?.core_ingredients || item?.ingredient);
    push('Suggested post care', item?.post_care);
    push('Rate card', item?.rate_card);
    push('Brand', item?.brand);
    push('Addresses', item?.conditions || item?.suggested_for_conditions);
    push('Goals', item?.skin_goals);
    const priceBits = [];
    if (item?.net_price != null && item?.net_price !== '') priceBits.push(`₹${item.net_price}`);
    if (
      item?.sale_price != null &&
      item?.sale_price !== '' &&
      String(item.sale_price) !== String(item.net_price)
    ) {
      priceBits.push(`sale ₹${item.sale_price}`);
    }
    if (item?.price && !priceBits.length) priceBits.push(String(item.price));
    if (item?.amount != null && item?.amount !== '' && !priceBits.length) {
      priceBits.push(`₹${item.amount}`);
    }
    if (priceBits.length) push('Price', priceBits.join(' · '));
    return sections;
  };

  const openTreatmentAboutPopup = (item) => {
    const title = item?.treatment || item?.name || 'Treatment details';
    const sections = buildTreatmentDetailSections(item);
    const text =
      item?.about_treatment ||
      item?.description ||
      item?.major_usp ||
      'No additional details available.';
    setAboutPopup({ open: true, title, text, sections });
  };

  const closeAboutPopup = () => setAboutPopup({ open: false, title: '', text: '', sections: [] });

  const getProductMatchPercent = (product, idx = 0) => {
    const raw = product?.match_score ?? product?.match_percent ?? product?.match ?? product?.similarity;
    if (raw != null && raw !== '') {
      const n = Number(raw);
      if (!Number.isNaN(n)) {
        if (n > 0 && n <= 1) return Math.round(n * 100);
        return Math.round(Math.max(0, Math.min(100, n)));
      }
    }
    const matched = Array.isArray(product?.matched_conditions) ? product.matched_conditions.length : 0;
    const concerns = Array.isArray(product?.concerns_treated) ? product.concerns_treated.length : 0;
    if (matched > 0) return Math.min(99, 82 + matched * 3);
    if (concerns > 0) return Math.min(97, 80 + concerns * 3);
    return Math.max(75, 94 - (idx % 6) * 3);
  };

  const toNumberOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const RUPEE_SYMBOL = '\u20B9';

  const formatPrice = (amount) => {
    if (amount == null || amount === '') return '-';
    const n = Number(amount);
    if (Number.isNaN(n)) return String(amount);
    return `${RUPEE_SYMBOL}${n.toFixed(0)}`;
  };

  const hasDiscount = (item) => {
    if (toNumberOrNull(item?.net_price) == null) return false;
    const d = item?.discount;
    if (d == null || d === '') return false;
    const n = Number(d);
    return !Number.isNaN(n) && n > 0;
  };

  const formatDiscountBadge = (item) => {
    const d = Number(item?.discount);
    if (Number.isNaN(d) || d <= 0) return null;
    if (d > 0 && d <= 1) return `${Math.round(d * 100)}% OFF`;
    if (d > 1 && d <= 100) return `${Math.round(d)}% OFF`;
    return `${RUPEE_SYMBOL}${Math.round(d)} OFF`;
  };

  const resolveTreatmentImageUrl = (url) => resolveSalonMediaUrl(url);
  const resolveProductImageUrl = (url) => resolveSalonMediaUrl(url);

  // Get user info
  const skinType = userDataFromState?.skinType || userDataFromState?.skin_type || 'Not specified';
  const gender = userDataFromState?.gender
    ? (salonGenderDisplayLabel(userDataFromState.gender) || String(userDataFromState.gender).trim())
    : 'Not specified';
  const userImage = capturedImageFromState || skinResultsFromState?.markedImage || null;
  const previewBaseImage = capturedImageFromState || skinResultsFromState?.capturedImage || userImage || null;
  const hasGender = Boolean(
    userDataFromState?.gender && String(userDataFromState.gender).trim(),
  );
  const hasSkinType = Boolean(skinType && skinType !== 'Not specified');
  const aiPhotoSkinType = formatVisionSkinType(photoInsight?.skin_type);
  const displaySkinType = aiPhotoSkinType || (hasSkinType
    ? formatVisionSkinType(String(skinType).trim().toLowerCase()) || String(skinType).trim()
    : null);
  const displaySkinColor = formatSkinColorLabel(photoInsight?.skin_color);
  const displayMacro = useMemo(() => {
    if (macroAnalysis?.regions && typeof macroAnalysis.regions === 'object') return macroAnalysis;
    if (photoInsight?.macro?.regions) return photoInsight.macro;
    if (effectiveState?.macroAnalysis?.regions) return effectiveState.macroAnalysis;
    return null;
  }, [macroAnalysis, photoInsight, effectiveState?.macroAnalysis]);

  const hasMacroScores = useMemo(() => {
    const regions = displayMacro?.regions;
    if (!regions || typeof regions !== 'object') return false;
    return Object.values(regions).some(
      (r) => r && typeof r === 'object' && (r.scores || r.macro_analysis?.scores),
    );
  }, [displayMacro]);
  const reportIdText = sessionId ? String(sessionId).slice(0, 14).toUpperCase() : null;
  const skinAgeValue = (() => {
    const raw = skinAge?.estimated_age ?? skinAge?.skin_age;
    return raw != null && !Number.isNaN(Number(raw)) ? Number(raw) : null;
  })();
  const skinAgeExplanation = (() => {
    const fromApi = typeof skinAge?.explanation === 'string' ? skinAge.explanation.trim() : '';
    if (isUsableSkinAgeExplanation(fromApi)) return fromApi;
    if (skinAgeValue != null) return buildSkinAgeExplanationFallback(skinAgeValue);
    return '';
  })();
  const displayEnvTip = isUsableEnvTip(envTip) ? envTip.trim() : '';
  const skinAgeDisplayReady = skinAgeValue != null && Boolean(skinAgeExplanation);
  const canEstimateSkinAge = Boolean(capturedImageFromState || skinResultsFromState?.capturedImage);
  const showSkinAgeSection = canEstimateSkinAge && (isSkinAgeLoading || skinAgeDisplayReady);
  const overallSkinScoreRaw = skinResultsFromState?.skinScore?.final_skin_score ?? skinResultsFromState?.skinScore?.score ?? null;
  const overallSkinScore = overallSkinScoreRaw != null && !Number.isNaN(Number(overallSkinScoreRaw))
    ? Math.max(0, Math.min(100, Math.round(Number(overallSkinScoreRaw))))
    : null;
  const previousOverallSkinScore = useMemo(() => {
    const prevRaw = previousSkinData?.skinScore?.final_skin_score
      ?? previousSkinData?.skinScore?.score
      ?? previousSkinData?.skin_score?.final_skin_score
      ?? previousSkinData?.skin_score?.score
      ?? previousSkinData?.skinScore
      ?? previousSkinData?.skin_score
      ?? null;
    if (prevRaw == null || Number.isNaN(Number(prevRaw))) return null;
    return Math.max(0, Math.min(100, Math.round(Number(prevRaw))));
  }, [previousSkinData]);
  const analysisDateValue = useMemo(() => {
    const rawDate = effectiveState?.analysisDate
      || effectiveState?.analysis_date
      || skinResultsFromState?.analysis_date
      || skinResultsFromState?.created_at
      || effectiveState?.createdAt
      || null;
    const parsed = rawDate ? new Date(rawDate) : new Date();
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [effectiveState, skinResultsFromState]);
  const analysisDateText = useMemo(() => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(analysisDateValue);
  }, [analysisDateValue]);
  const previousAnalysisDateValue = useMemo(() => {
    const rawDate = previousSkinData?.created_at || previousSkinData?.analysis_date || null;
    if (!rawDate) return null;
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }, [previousSkinData]);
  const previousAnalysisDateText = useMemo(() => {
    if (!previousAnalysisDateValue) return null;
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(previousAnalysisDateValue);
  }, [previousAnalysisDateValue]);
  const radarDaysBetween = useMemo(() => {
    if (!previousAnalysisDateValue || !analysisDateValue) return null;
    const diffMs = analysisDateValue.getTime() - previousAnalysisDateValue.getTime();
    return Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
  }, [analysisDateValue, previousAnalysisDateValue]);

  // Concerns split by severity (score < 75) vs good (score >= 75)
  const concernRadarOptions = useMemo(() => ({
    showSkinConcerns,
    showSkinConditions,
    segmentationAllowedNames,
    visibleConditionNames,
  }), [showSkinConcerns, showSkinConditions, segmentationAllowedNames, visibleConditionNames]);

  const concernsBySeverity = useMemo(() => {
    const skinScore = skinResultsFromState?.skinScore || {};
    const labelScores = skinScore?.skin_label_scores && typeof skinScore.skin_label_scores === 'object'
      ? skinScore.skin_label_scores
      : null;
    return buildConcernItemsList({
      detections: skinResultsFromState?.detections || [],
      skinConditions: skinResultsFromState?.skinConditions || {},
      labelScores,
      ...concernRadarOptions,
    });
  }, [skinResultsFromState, concernRadarOptions]);

  const concernRadarItems = useMemo(
    () => buildConcernRadarItemsFromList(concernsBySeverity.allItems),
    [concernsBySeverity.allItems],
  );

  const radarModel = useMemo(() => {
    if (!concernRadarItems.length) return null;
    const size = 380;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 108;
    const axis = concernRadarItems.map((item, idx) => {
      const angle = ((Math.PI * 2 * idx) / concernRadarItems.length) - Math.PI / 2;
      const dataRadius = (item.intensity / 100) * radius;
      const pointX = cx + Math.cos(angle) * dataRadius;
      const pointY = cy + Math.sin(angle) * dataRadius;
      const cos = Math.cos(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
      const labelX = cx + Math.cos(angle) * (radius + 14);
      const labelY = cy + Math.sin(angle) * (radius + 14);
      return {
        ...item,
        angle,
        pointX,
        pointY,
        labelX,
        labelY,
        labelAnchor,
        labelOffsetX,
      };
    });
    const polygon = axis.map((a) => `${a.pointX},${a.pointY}`).join(' ');
    return { size, cx, cy, radius, axis, polygon, levels: [20, 40, 60, 80, 100] };
  }, [concernRadarItems]);

  const previousConcernRadarItems = useMemo(() => {
    const { allItems } = buildConcernItemsFromPreviousPayload(previousSkinData, concernRadarOptions);
    return buildConcernRadarItemsFromList(allItems);
  }, [previousSkinData, concernRadarOptions]);

  const previousRadarModel = useMemo(() => {
    if (!previousConcernRadarItems.length) return null;
    const size = 380;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 108;
    const axis = previousConcernRadarItems.map((item, idx) => {
      const angle = ((Math.PI * 2 * idx) / previousConcernRadarItems.length) - Math.PI / 2;
      const dataRadius = (item.intensity / 100) * radius;
      const pointX = cx + Math.cos(angle) * dataRadius;
      const pointY = cy + Math.sin(angle) * dataRadius;
      const cos = Math.cos(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
      const labelX = cx + Math.cos(angle) * (radius + 14);
      const labelY = cy + Math.sin(angle) * (radius + 14);
      return {
        ...item,
        angle,
        pointX,
        pointY,
        labelX,
        labelY,
        labelAnchor,
        labelOffsetX,
      };
    });
    const polygon = axis.map((a) => `${a.pointX},${a.pointY}`).join(' ');
    return { size, cx, cy, radius, axis, polygon, levels: [20, 40, 60, 80, 100] };
  }, [previousConcernRadarItems]);

  const genericSummaryFromValues = useMemo(() => {
    const score = Number(overallSkinScore || 0);
    const boundedScore = Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : null;
    const weakest = [...concernRadarItems].sort((a, b) => a.score - b.score).slice(0, 2);
    const weakText = weakest.length
      ? weakest.map((item) => `${item.label} (${getScoreTone(item.score)})`).join(' and ')
      : 'overall skin balance';
    const scoreText = boundedScore == null
      ? 'Your current skin scan is complete.'
      : boundedScore < 60
        ? 'Your skin currently needs focused recovery support.'
        : boundedScore < 80
          ? 'Your skin is in a moderate state with good recovery potential.'
          : 'Your skin looks strong and well-maintained.';
    return `${scoreText} Your key focus areas right now are ${weakText}. Follow your personalized routine consistently to see visible progress in upcoming scans.`;
  }, [overallSkinScore, concernRadarItems, getScoreTone]);

  const historyTrackingSummary = useMemo(() => {
    if (!previousConcernRadarItems.length || !concernRadarItems.length) return '';
    const prevMap = new Map(
      previousConcernRadarItems.map((item) => [normalizeConditionName(item.class_name), Number(item.score || 0)])
    );
    const compared = concernRadarItems
      .map((item) => {
        const prev = prevMap.get(normalizeConditionName(item.class_name));
        if (!Number.isFinite(prev)) return null;
        return {
          label: item.label,
          delta: Math.round((Number(item.score || 0) - prev) * 10) / 10,
        };
      })
      .filter(Boolean);
    if (!compared.length) return '';

    const improved = [...compared].filter((item) => item.delta >= 3).sort((a, b) => b.delta - a.delta);
    const declined = [...compared].filter((item) => item.delta <= -3).sort((a, b) => a.delta - b.delta);

    const improvedText = improved.slice(0, 3).map((item) => item.label).join(', ');
    const declinedText = declined.slice(0, 3).map((item) => item.label).join(', ');

    if (improved.length && !declined.length) {
      return `Great progress since your previous scan: ${improvedText}. Keep this momentum going.`;
    }
    if (declined.length && !improved.length) {
      return `Compared with your previous scan, ${declinedText} has dropped. A little extra consistency this week can help you bounce back.`;
    }
    if (improved.length && declined.length) {
      return `Since your previous scan, you improved in ${improvedText}, while ${declinedText} needs attention. This trend is normal and can be balanced with routine consistency.`;
    }
    return 'Compared with your previous scan, your skin profile is mostly stable. Staying consistent will help convert stability into visible improvement.';
  }, [previousConcernRadarItems, concernRadarItems]);

  const effectiveSummaryText = useMemo(() => {
    const base = (typeof summary === 'string' && summary.trim()) ? summary.trim() : genericSummaryFromValues;
    const combined = historyTrackingSummary ? `${base}\n\n${historyTrackingSummary}` : base;
    return replaceScoreMentionsWithFriendlyWords(combined);
  }, [summary, genericSummaryFromValues, historyTrackingSummary, replaceScoreMentionsWithFriendlyWords]);

  const radarComparisonModel = useMemo(() => {
    if (!previousConcernRadarItems.length || !concernRadarItems.length) return null;
    const size = 380;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 108;

    const afterMap = new Map(
      concernRadarItems.map((item) => [resolveConcernKey(item.class_name), item])
    );
    const beforeMap = new Map(
      previousConcernRadarItems.map((item) => [resolveConcernKey(item.class_name), item])
    );
    const orderedKeys = [
      ...Array.from(afterMap.keys()),
      ...Array.from(beforeMap.keys()).filter((key) => !afterMap.has(key)),
    ];

    const axis = orderedKeys.map((key, idx) => {
      const afterItem = afterMap.get(key) || null;
      const beforeItem = beforeMap.get(key) || null;
      const angle = ((Math.PI * 2 * idx) / orderedKeys.length) - Math.PI / 2;
      const afterIntensity = afterItem ? Math.max(5, Math.min(100, Number(afterItem.score || 0))) : 0;
      const beforeIntensity = beforeItem ? Math.max(5, Math.min(100, Number(beforeItem.score || 0))) : 0;
      const afterRadius = (afterIntensity / 100) * radius;
      const beforeRadius = (beforeIntensity / 100) * radius;
      const cos = Math.cos(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
      const beforeScore = beforeItem ? Math.round(Number(beforeItem.score || 0)) : null;
      const afterScore = afterItem ? Math.round(Number(afterItem.score || 0)) : null;
      const delta = Number.isFinite(beforeScore) && Number.isFinite(afterScore)
        ? Math.round((afterScore - beforeScore) * 10) / 10
        : null;
      const changeKind = !beforeItem && afterItem
        ? 'new'
        : beforeItem && !afterItem
          ? 'missing'
          : Number.isFinite(delta)
            ? delta >= 3
              ? 'improved'
              : delta <= -3
                ? 'declined'
                : 'stable'
            : 'stable';

      // All labels at equal distance from radar edge
      const labelDistance = 18;

      return {
        key,
        label: getConditionDisplayName(afterItem?.class_name || beforeItem?.class_name || key),
        angle,
        hasAfter: Boolean(afterItem),
        hasBefore: Boolean(beforeItem),
        afterScore,
        beforeScore,
        delta,
        changeKind,
        afterPointX: cx + Math.cos(angle) * afterRadius,
        afterPointY: cy + Math.sin(angle) * afterRadius,
        beforePointX: cx + Math.cos(angle) * beforeRadius,
        beforePointY: cy + Math.sin(angle) * beforeRadius,
        labelX: cx + Math.cos(angle) * (radius + labelDistance),
        labelY: cy + Math.sin(angle) * (radius + labelDistance),
        labelAnchor,
        labelOffsetX,
      };
    });

    return {
      size,
      cx,
      cy,
      radius,
      levels: [20, 40, 60, 80, 100],
      axis,
      afterPolygon: axis.map((a) => `${a.afterPointX},${a.afterPointY}`).join(' '),
      beforePolygon: axis.map((a) => `${a.beforePointX},${a.beforePointY}`).join(' '),
    };
  }, [concernRadarItems, previousConcernRadarItems]);

  const hasBeforeAfterRadar = Boolean(
    radarComparisonModel
    && previousRadarModel
    && radarModel
    && isPreviousScanComparable(
      previousSkinData,
      concernsBySeverity.allItems,
      previousConcernRadarItems,
      analysisDateValue,
    ),
  );
  const isAfterComparisonView = hasBeforeAfterRadar && radarComparisonTab === 'after';
  const activeRadarModel = hasBeforeAfterRadar
    ? (radarComparisonTab === 'before' ? previousRadarModel : radarComparisonModel)
    : radarModel;
  const activeRadarScore = hasBeforeAfterRadar && radarComparisonTab === 'before' ? previousOverallSkinScore : overallSkinScore;
  const activeRadarDate = hasBeforeAfterRadar && radarComparisonTab === 'before' ? previousAnalysisDateText : analysisDateText;
  const activeRadarConcernDetails = useMemo(() => {
    if (!activeRadarModel?.axis?.length) return [];
    if (isAfterComparisonView) {
      return activeRadarModel.axis.map((item, idx) => ({
        idx,
        label: item.label,
        beforeScore: item.beforeScore,
        afterScore: item.afterScore,
        delta: item.delta,
        changeKind: item.changeKind || 'stable',
      }));
    }
    return activeRadarModel.axis.map((item, idx) => ({
      idx,
      label: item.label,
      beforeScore: hasBeforeAfterRadar ? item.score : null,
      afterScore: hasBeforeAfterRadar ? null : item.score,
      delta: null,
      changeKind: 'stable',
    }));
  }, [activeRadarModel, hasBeforeAfterRadar, isAfterComparisonView]);

  const activeRadarConcernDetail = useMemo(() => {
    if (!activeRadarConcernDetails.length) return null;
    const safeIndex = Math.max(0, Math.min(radarConcernModal.index, activeRadarConcernDetails.length - 1));
    return activeRadarConcernDetails[safeIndex] || null;
  }, [activeRadarConcernDetails, radarConcernModal.index]);

  const openRadarConcernModal = useCallback((index) => {
    if (!activeRadarConcernDetails.length) return;
    const safeIndex = Math.max(0, Math.min(index, activeRadarConcernDetails.length - 1));
    setRadarConcernModal({ open: true, index: safeIndex });
  }, [activeRadarConcernDetails]);

  const closeRadarConcernModal = useCallback(() => {
    setRadarConcernModal((prev) => ({ ...prev, open: false }));
  }, []);

  const stepRadarConcernModal = useCallback((step) => {
    setRadarConcernModal((prev) => {
      const total = activeRadarConcernDetails.length;
      if (!total) return prev;
      const nextIndex = (prev.index + step + total) % total;
      return { ...prev, index: nextIndex };
    });
  }, [activeRadarConcernDetails.length]);


  const detailedConcernItems = useMemo(() => {
    return sortConcernsBySeverity(
      [...concernsBySeverity.allItems].map((item) => ({
        ...item,
        label: getConditionDisplayName(item.class_name),
      })),
    );
  }, [concernsBySeverity.allItems]);

  const healthScoreItems = detailedConcernItems;
  const improvementPotential = useMemo(
    () => computeImprovementPotential(healthScoreItems),
    [healthScoreItems],
  );
  const displayTips = useMemo(() => {
    const source = reportTips.length
      ? reportTips
      : buildFallbackTips(healthScoreItems, displaySkinType);
    return source
      .map((t) => sanitizeTipText(t))
      .filter((t) => t && t.length > 8)
      .slice(0, 6);
  }, [reportTips, healthScoreItems, displaySkinType]);
  const shortWhatThisMeans = useMemo(() => {
    const raw = (summary || '').trim();
    if (!raw) return '';
    const plain = stripHtml(raw).replace(/\s+/g, ' ').trim();
    const words = plain.split(' ');
    if (words.length <= 55) return plain;
    return `${words.slice(0, 55).join(' ').replace(/[.,;:]+$/, '')}.`;
  }, [summary]);


  const selectedConcernItem = useMemo(() => {
    if (!selectedConcernName) return null;
    return detailedConcernItems.find((item) => item.class_name === selectedConcernName) || null;
  }, [detailedConcernItems, selectedConcernName]);

  const availableConcernViews = useMemo(() => {
    const views = ['front'];
    const getSideImage = (item) => (
      item?.captured_image
      || item?.capturedImage
      || item?.pipeline?.captured_image
      || item?.raw_image
      || item?.marked_image
      || null
    );
    const hasLeft = Boolean(
      sideCaptures?.left ||
      sideResults.some((item) => item?.key === 'left' && getSideImage(item))
    );
    const hasRight = Boolean(
      sideCaptures?.right ||
      sideResults.some((item) => item?.key === 'right' && getSideImage(item))
    );
    if (hasLeft) views.push('left');
    if (hasRight) views.push('right');
    return views;
  }, [sideCaptures, sideResults]);

  const hasMultiAnglePreview = availableConcernViews.length > 1;
  const isSelectedSkinConditionConcern = Boolean(
    selectedConcernName && isSkinConditionName(selectedConcernName),
  );
  const showConcernAngleNavigation = hasMultiAnglePreview && !isSelectedSkinConditionConcern;

  const moveConcernPreviewBy = useCallback((direction) => {
    if (!showConcernAngleNavigation) return;
    const idx = availableConcernViews.indexOf(selectedConcernView);
    const currentIndex = idx >= 0 ? idx : 0;
    const nextIndex = (currentIndex + direction + availableConcernViews.length) % availableConcernViews.length;
    setSelectedConcernView(availableConcernViews[nextIndex]);
  }, [availableConcernViews, showConcernAngleNavigation, selectedConcernView]);

  const drawConcernPreviewDetections = useCallback((ctx, conditionName, detectionsToUse, imgWidth) => {
    if (!ctx || !conditionName || !Array.isArray(detectionsToUse) || detectionsToUse.length === 0) return;
    const drawListRaw = getDetectionsToDraw(conditionName, detectionsToUse, imgWidth);

    const iou = (a, b) => {
      const bbA = a?.bbox || [];
      const bbB = b?.bbox || [];
      if (bbA.length !== 4 || bbB.length !== 4) return 0;
      const [ax1, ay1, ax2, ay2] = bbA;
      const [bx1, by1, bx2, by2] = bbB;
      const ix1 = Math.max(ax1, bx1);
      const iy1 = Math.max(ay1, by1);
      const ix2 = Math.min(ax2, bx2);
      const iy2 = Math.min(ay2, by2);
      if (ix2 <= ix1 || iy2 <= iy1) return 0;
      const inter = (ix2 - ix1) * (iy2 - iy1);
      const areaA = Math.max(1, (ax2 - ax1) * (ay2 - ay1));
      const areaB = Math.max(1, (bx2 - bx1) * (by2 - by1));
      return inter / (areaA + areaB - inter);
    };

    const clusters = [];
    const used = new Array(drawListRaw.length).fill(false);
    for (let i = 0; i < drawListRaw.length; i += 1) {
      if (used[i]) continue;
      const base = drawListRaw[i];
      const cluster = [base];
      used[i] = true;
      for (let j = i + 1; j < drawListRaw.length; j += 1) {
        if (used[j]) continue;
        if (iou(base, drawListRaw[j]) >= 0.75) {
          used[j] = true;
          cluster.push(drawListRaw[j]);
        }
      }
      clusters.push(cluster);
    }

    const drawList = clusters.map((list) => {
      const best = list.reduce((acc, d) => ((d?.confidence ?? 0) > (acc?.confidence ?? 0) ? d : acc), list[0]);
      return { det: best, stackedCount: list.length };
    });

    drawList.forEach(({ det, stackedCount }) => {
      if (det?.ui_hidden === true) return;
      const bbox = det?.bbox || [];
      if (bbox.length !== 4) return;
      const [x1, y1, x2, y2] = bbox;
      const classId = det.class_id;
      const color = getColorByClassId(classId);
      const centerX = (x1 + x2) / 2;
      const centerY = (y1 + y2) / 2;
      const width = x2 - x1;
      const height = y2 - y1;
      const radius = Math.max(width, height) / 2;
      const suppressOutlines = false;

      ctx.beginPath();
      if (classId === 4 || classId === 6 || classId === 12 || classId === 14 || classId === 1 || classId === 2 || classId === 9 || classId === 5 || classId === 10) {
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      } else if (classId === 0 || classId === 11) {
        const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
        const r = rgbMatch ? rgbMatch[1] : '0';
        const g = rgbMatch ? rgbMatch[2] : '191';
        const b = rgbMatch ? rgbMatch[3] : '255';
        const featheredRadius = radius * 1.2;
        const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, featheredRadius);
        gradient.addColorStop(0, `rgba(${r},${g},${b},0.35)`);
        gradient.addColorStop(0.45, `rgba(${r},${g},${b},0.18)`);
        gradient.addColorStop(0.75, `rgba(${r},${g},${b},0.06)`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);
        const cornerRadius = Math.min(width, height) * 0.2;
        ctx.moveTo(x1 + cornerRadius, y1);
        ctx.lineTo(x2 - cornerRadius, y1);
        ctx.arcTo(x2, y1, x2, y1 + cornerRadius, cornerRadius);
        ctx.lineTo(x2, y2 - cornerRadius);
        ctx.arcTo(x2, y2, x2 - cornerRadius, y2, cornerRadius);
        ctx.lineTo(x1 + cornerRadius, y2);
        ctx.arcTo(x1, y2, x1, y2 - cornerRadius, cornerRadius);
        ctx.lineTo(x1, y1 + cornerRadius);
        ctx.arcTo(x1, y1, x1 + cornerRadius, y1, cornerRadius);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
      } else if (classId === 3 || classId === 15) {
        const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
        const r = rgbMatch ? rgbMatch[1] : '200';
        const g = rgbMatch ? rgbMatch[2] : '0';
        const b = rgbMatch ? rgbMatch[3] : '255';
        const offsetUp = radius * 0.22;
        const cy = centerY - offsetUp;
        const featheredRadius = radius * 1.25;
        const gradient = ctx.createRadialGradient(centerX, cy, 0, centerX, cy, featheredRadius);
        gradient.addColorStop(0, `rgba(${r},${g},${b},0.65)`);
        gradient.addColorStop(0.3, `rgba(${r},${g},${b},0.48)`);
        gradient.addColorStop(0.55, `rgba(${r},${g},${b},0.32)`);
        gradient.addColorStop(0.78, `rgba(${r},${g},${b},0.2)`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},0.18)`);
        const curveBulge = radius * 0.32;
        ctx.beginPath();
        ctx.arc(centerX, cy, radius, 0, Math.PI);
        ctx.quadraticCurveTo(centerX, cy + curveBulge, centerX + radius, cy);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(centerX, cy, radius, 0, Math.PI);
        ctx.quadraticCurveTo(centerX, cy + curveBulge, centerX + radius, cy);
        ctx.closePath();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.lineWidth = 2;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.stroke();
      } else {
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }

      if (!suppressOutlines && stackedCount && stackedCount > 1) {
        const extra = Math.min(3, stackedCount - 1);
        for (let k = 1; k <= extra; k += 1) {
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.8;
          ctx.setLineDash([4, 3]);
          if (classId === 0 || classId === 11) {
            const pad = 3 * k;
            ctx.strokeRect(x1 - pad, y1 - pad, Math.max(1, (x2 - x1) + pad * 2), Math.max(1, (y2 - y1) + pad * 2));
          } else {
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius + (3 * k), 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.restore();
        }
      }

      if (!suppressOutlines && det?.low_confidence) {
        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x1, y1, Math.max(1, x2 - x1), Math.max(1, y2 - y1));
        ctx.restore();
      }
    });
  }, []);

  const getFaceSideFilterContext = useCallback((sideResult, imageWidth) => {
    const fallbackMid = Number(imageWidth) > 0 ? imageWidth / 2 : null;
    const roiPayload = sideResult?.roi_region_landmarks_px || {};
    const faceRegion = roiPayload?.face;
    const anchors = Array.isArray(faceRegion?.anchor_landmarks_px) ? faceRegion.anchor_landmarks_px : [];

    const leftCheek = anchors.find((pt) => Number(pt?.i) === 234 && Number.isFinite(Number(pt?.x)));
    const rightCheek = anchors.find((pt) => Number(pt?.i) === 454 && Number.isFinite(Number(pt?.x)));
    const isMirrored = Boolean(leftCheek && rightCheek && Number(leftCheek.x) > Number(rightCheek.x));

    // MediaPipe landmark #1 is nose tip; it's the most reliable face midline anchor.
    const noseTip = anchors.find((pt) => Number(pt?.i) === 1 && Number.isFinite(Number(pt?.x)));
    const getRegionCenterX = (regionName) => {
      const region = roiPayload?.[regionName];
      const roiBbox = region?.roi_bbox;
      if (roiBbox && Number.isFinite(Number(roiBbox.x1)) && Number.isFinite(Number(roiBbox.x2))) {
        return (Number(roiBbox.x1) + Number(roiBbox.x2)) / 2;
      }
      const regionAnchors = Array.isArray(region?.anchor_landmarks_px) ? region.anchor_landmarks_px : [];
      const xs = regionAnchors.map((pt) => Number(pt?.x)).filter((x) => Number.isFinite(x));
      if (xs.length > 0) return xs.reduce((sum, x) => sum + x, 0) / xs.length;
      return null;
    };

    let midX = fallbackMid;
    if (noseTip) {
      midX = Number(noseTip.x);
    } else {
      const anchorXs = anchors
        .map((pt) => Number(pt?.x))
        .filter((x) => Number.isFinite(x));
      if (anchorXs.length > 0) {
        const minX = Math.min(...anchorXs);
        const maxX = Math.max(...anchorXs);
        midX = (minX + maxX) / 2;
      } else {
        const bbox = faceRegion?.roi_bbox;
        if (bbox && Number.isFinite(Number(bbox.x1)) && Number.isFinite(Number(bbox.x2))) {
          midX = (Number(bbox.x1) + Number(bbox.x2)) / 2;
        }
      }
    }

    const leftCheekX = getRegionCenterX('left_cheek');
    const rightCheekX = getRegionCenterX('right_cheek');

    let leftCaptureHalf = isMirrored ? 'right' : 'left';
    let rightCaptureHalf = isMirrored ? 'left' : 'right';

    if (Number.isFinite(midX) && Number.isFinite(leftCheekX) && Number.isFinite(rightCheekX)) {
      leftCaptureHalf = leftCheekX >= midX ? 'right' : 'left';
      rightCaptureHalf = rightCheekX >= midX ? 'right' : 'left';
      if (leftCaptureHalf === rightCaptureHalf) {
        rightCaptureHalf = leftCaptureHalf === 'right' ? 'left' : 'right';
      }
    } else if (Number.isFinite(midX) && Number.isFinite(rightCheekX)) {
      rightCaptureHalf = rightCheekX >= midX ? 'right' : 'left';
      leftCaptureHalf = rightCaptureHalf === 'right' ? 'left' : 'right';
    } else if (Number.isFinite(midX) && Number.isFinite(leftCheekX)) {
      leftCaptureHalf = leftCheekX >= midX ? 'right' : 'left';
      rightCaptureHalf = leftCaptureHalf === 'right' ? 'left' : 'right';
    }

    return {
      midX,
      leftCaptureHalf,
      rightCaptureHalf,
    };
  }, []);

  const filterDetectionsForSelectedView = useCallback((detections, view, faceFilterContext, imageWidth) => {
    if (!Array.isArray(detections) || detections.length === 0) return [];
    if (view === 'front') return detections;

    const midX = Number.isFinite(faceFilterContext?.midX)
      ? faceFilterContext.midX
      : (Number(imageWidth) > 0 ? imageWidth / 2 : null);
    if (!Number.isFinite(midX)) return detections;

    const targetHalf = view === 'left'
      ? (faceFilterContext?.rightCaptureHalf || 'right')
      : (faceFilterContext?.leftCaptureHalf || 'left');

    return detections.filter((det) => {
      const bbox = Array.isArray(det?.bbox) ? det.bbox : [];
      if (bbox.length !== 4) return true;
      const [x1, , x2] = bbox;
      const centerX = (Number(x1) + Number(x2)) / 2;
      if (!Number.isFinite(centerX)) return true;
      return targetHalf === 'right' ? centerX >= midX : centerX <= midX;
    });
  }, []);

  const renderSelectedConcernPreview = useCallback((conditionName, view = selectedConcernView) => {
    const sideResult = sideResults.find((item) => item?.key === view) || null;
    const sideImageFromResult = sideResult?.raw_image
      || sideResult?.captured_image
      || sideResult?.capturedImage
      || sideResult?.pipeline?.captured_image
      || null;
    const sideRawImage = view === 'left'
      ? (sideCaptures?.left || sideImageFromResult)
      : view === 'right'
        ? (sideCaptures?.right || sideImageFromResult)
        : null;
    const sideFallbackImage = sideRawImage || sideResult?.marked_image || null;
    const baseImage = view === 'front' ? previewBaseImage : (sideRawImage || null);

    if (!conditionName) {
      if (view === 'front') {
        setSelectedConcernPreviewImage(skinResultsFromState?.markedImage || previewBaseImage || null);
      } else {
        setSelectedConcernPreviewImage(sideFallbackImage);
      }
      return;
    }

    const skinConditions = skinResultsFromState?.skinConditions || {};
    if (view === 'front' && isSkinConditionName(conditionName)) {
      const canonical = canonicalSkinConditionName(conditionName) || conditionName;
      let maskUrl = getSkinConditionKeyCandidates(canonical)
        .map((key) => skinConditions?.[key]?.mask)
        .find((url) => typeof url === 'string' && url) || null;
      if (typeof maskUrl === 'string' && maskUrl.startsWith('/') && API_BASE_URL) {
        maskUrl = `${API_BASE_URL.replace(/\/$/, '')}${maskUrl}`;
      }
      if (maskUrl) {
        setSelectedConcernPreviewImage(maskUrl);
        return;
      }
      if (sessionId) {
        fetchSkinConcernMask({
          sessionId,
          displayName: canonical,
          appSessionToken: token || null,
        }).then((fetchedMask) => {
          if (fetchedMask) {
            setSelectedConcernPreviewImage(fetchedMask);
          }
        }).catch(() => {});
      }
    }

    if (!baseImage) {
      setSelectedConcernPreviewImage(view === 'front' ? (skinResultsFromState?.markedImage || null) : null);
      return;
    }

    const sideDetections = Array.isArray(sideResult?.detections)
      ? sideResult.detections
      : (Array.isArray(sideResult?.pipeline?.detections) ? sideResult.pipeline.detections : []);

    const sourceDetectionsRaw = view === 'front'
      ? (skinResultsFromState?.frontDetections || skinResultsFromState?.detections || [])
      : sideDetections;
    const sourceDetections = Array.isArray(sourceDetectionsRaw) ? sourceDetectionsRaw : [];

    const filteredDetections = sourceDetections.filter((det) => {
      const className = normalizeConditionName(det?.class_name || det?.className || 'Unknown');
      return className === conditionName;
    });

    if (!filteredDetections.length) {
      setSelectedConcernPreviewImage(baseImage);
      return;
    }

    const canvas = concernPreviewCanvasRef.current;
    if (!canvas) {
      setSelectedConcernPreviewImage(baseImage);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const faceFilterContext = getFaceSideFilterContext(sideResult, img.width);
        const detectionsForView = filterDetectionsForSelectedView(filteredDetections, view, faceFilterContext, img.width);

        if (!detectionsForView.length) {
          setSelectedConcernPreviewImage(baseImage);
          return;
        }

        drawConcernPreviewDetections(ctx, conditionName, detectionsForView, img.width);
        setSelectedConcernPreviewImage(canvas.toDataURL('image/jpeg', 0.95));
      } catch {
        setSelectedConcernPreviewImage(baseImage);
      }
    };
    img.onerror = () => {
      setSelectedConcernPreviewImage(baseImage);
    };
    img.src = baseImage;
  }, [
    API_BASE_URL,
    drawConcernPreviewDetections,
    filterDetectionsForSelectedView,
    getFaceSideFilterContext,
    previewBaseImage,
    selectedConcernView,
    sessionId,
    sideCaptures,
    sideResults,
    skinResultsFromState,
    token,
  ]);

  useEffect(() => {
    if (!sessionId || sideCaptures?.left || sideCaptures?.right) return;
    let cancelled = false;
    void getSideCaptures(sessionId).then((stored) => {
      if (cancelled || !stored) return;
      setRestoredSideCaptures((prev) => prev || stored);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, sideCaptures]);

  useEffect(() => {
    if (!availableConcernViews.length) return;
    if (!availableConcernViews.includes(selectedConcernView)) {
      setSelectedConcernView(availableConcernViews[0]);
    }
  }, [availableConcernViews, selectedConcernView]);

  useEffect(() => {
    if (!isSelectedSkinConditionConcern) return;
    if (selectedConcernView !== 'front') {
      setSelectedConcernView('front');
    }
  }, [isSelectedSkinConditionConcern, selectedConcernView]);

  useEffect(() => {
    renderSelectedConcernPreview(selectedConcernName, selectedConcernView);
  }, [renderSelectedConcernPreview, selectedConcernName, selectedConcernView]);

  useEffect(() => {
    if (!detailedConcernItems.length) {
      setSelectedConcernName(null);
      return;
    }
    const hasCurrentSelection = detailedConcernItems.some((item) => item.class_name === selectedConcernName);
    if (!hasCurrentSelection) {
      setSelectedConcernName(detailedConcernItems[0].class_name);
    }
  }, [detailedConcernItems, selectedConcernName]);

  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, {
      ...current,
      sessionId,
      selectedCondition: selectedConcernName || null,
      imageView: selectedConcernView || 'front',
    });
  }, [sessionId, selectedConcernName, selectedConcernView]);

  const pdfQrReady = Boolean(sessionId);

  // Keep capture restore values in refs so createPdfDownloadUrl identity stays stable.
  // Otherwise concern/view updates mid-capture re-trigger the QR effect and cancel the QR.
  const selectedConcernNameRef = useRef(selectedConcernName);
  const selectedConcernViewRef = useRef(selectedConcernView);
  selectedConcernNameRef.current = selectedConcernName;
  selectedConcernViewRef.current = selectedConcernView;

  const createPdfDownloadUrl = useCallback(async () => {
    if (!pdfQrReady) throw new Error('Report is still generating');
    if (!sessionId) throw new Error('session_id is required');

    const payload = await buildSalonReportPdfPayload({
      sessionId,
      token,
      name: userDataFromState?.name || 'Guest',
      summary: shortWhatThisMeans || summary || '',
      routine,
      skinScore: skinResultsFromState?.skinScore || overallSkinScore,
      gender: hasGender ? gender : null,
      skinType: displaySkinType,
      skinAge: {
        estimated_age: skinAgeValue,
        explanation: skinAgeExplanation,
      },
      aqiData,
      envTip: displayEnvTip,
      healthScoreItems,
      improvementPotential,
      displayTips,
      displayInsight: '',
      userImage,
      previewBaseImage,
      detections: normalizeDetections(
        Array.isArray(skinResultsFromState?.frontDetections)
          ? skinResultsFromState.frontDetections
          : (Array.isArray(skinResultsFromState?.detections)
            ? skinResultsFromState.detections
            : []),
      ),
      skinConditions: skinResultsFromState?.skinConditions || null,
      aiConditionDescriptions,
      recommendedProducts,
      recommendedTreatments,
      displayMacro,
      orgName,
      orgLogoUrl,
      analysisDateText,
      reportIdText,
      apiBaseUrl: API_BASE_URL,
    });

    return createSalonReportPdfDownloadUrl(payload, { timeoutMs: 120000 });
  }, [
    pdfQrReady,
    sessionId,
    token,
    userDataFromState?.name,
    shortWhatThisMeans,
    summary,
    routine,
    skinResultsFromState,
    overallSkinScore,
    hasGender,
    gender,
    displaySkinType,
    skinAgeValue,
    skinAgeExplanation,
    aqiData,
    displayEnvTip,
    healthScoreItems,
    improvementPotential,
    displayTips,
    userImage,
    previewBaseImage,
    aiConditionDescriptions,
    recommendedProducts,
    recommendedTreatments,
    displayMacro,
    orgName,
    orgLogoUrl,
    analysisDateText,
    reportIdText,
    API_BASE_URL,
  ]);

  return (
    <div
      className="skin-routine-page-shell page-bg"
      id="skin-routine-page-shell"
      ref={scrollShellRef}
      style={{ '--routine-card-bg-image': `url(${process.env.PUBLIC_URL}/BG/BG.png)` }}
    >
      {/* Navigation Buttons */}
      <div className="skin-routine-page-nav skin-report-nav" id="skin-routine-page-nav">
        <div className="skin-report-nav-left">
          <div className="skin-routine-page-brand skin-report-nav-brand">
            <FaceIdentityBrandLogo
              showLogo={showLogo}
              orgLogoUrl={orgLogoUrl}
              onLogoError={onLogoError}
              className="skin-routine-page-brand-logo"
              imgClassName="skin-routine-page-brand-logo-img"
              fallbackClassName="skin-routine-page-brand-fallback"
              alt={orgName || 'Organization'}
            />
            {orgName ? (
              <div className={`skin-report-brand-copy${showLogo ? ' skin-report-brand-copy--with-logo' : ''}`}>
                <span className="skin-report-brand-org">{orgName}</span>
                <span className="skin-report-brand-title">AI Skin Analysis Report</span>
              </div>
            ) : null}
          </div>
        </div>
        <div className="skin-routine-page-nav-right skin-report-nav-right" data-pdf-exclude="true">
          <SalonHeaderActions homeButtonClassName="skin-routine-page-home-button" onHome={handleHome} />
        </div>
      </div>

      <main className="skin-routine-page-main skin-report-page-main" id="skin-routine-page-main" ref={scrollMainRef}>
        <div className="skin-routine-page-container" id="skin-routine-page-container">
          <section
            className={`skin-routine-page-layout skin-report-root${pdfCaptureMode ? ' is-pdf-capturing' : ''}`}
            id="skin-routine-page-layout"
          >

            <div className="skin-report-pdf-block" data-pdf-page="intro">
              <header className="skin-report-masthead" aria-label="Report introduction">
                <div className="skin-report-masthead-copy">
                  <p className="skin-report-masthead-kicker">Beauty Intelligence</p>
                  <h1 className="skin-report-masthead-title">
                    {orgName ? `${orgName} Skin Report` : 'AI Skin Analysis Report'}
                  </h1>
                  <p className="skin-report-masthead-sub">
                    Personalized insights from your scan
                  </p>
                </div>
                {(analysisDateText || reportIdText || (hasGender && gender)) ? (
                  <div className="skin-report-masthead-chips" aria-label="Report summary">
                    {analysisDateText ? <span className="skin-report-masthead-chip"><strong>Scan</strong> {analysisDateText}</span> : null}
                    {reportIdText ? <span className="skin-report-masthead-chip"><strong>ID</strong> {reportIdText}</span> : null}
                    {hasGender && gender ? <span className="skin-report-masthead-chip"><strong>Gender</strong> {gender}</span> : null}
                  </div>
                ) : null}
              </header>

              <ReportPdfQrPanel
                enabled={pdfQrReady}
                createDownloadUrl={createPdfDownloadUrl}
                ctaLabel="Download report"
                title="Scan to download PDF"
                subtitle="Scan with your phone to save your report as a PDF."
                className="skin-report-pdf-qr"
              />

              <SkinReportHero
                faceImage={pdfCaptureMode
                  ? (previewBaseImage || userImage)
                  : (selectedConcernPreviewImage || userImage)}
                concernLabel={pdfCaptureMode ? null : (selectedConcernItem?.label || null)}
                concernPreviewCanvasRef={concernPreviewCanvasRef}
                showAngleNav={!pdfCaptureMode && showConcernAngleNavigation}
                concernView={selectedConcernView}
                onPrevAngle={() => moveConcernPreviewBy(-1)}
                onNextAngle={() => moveConcernPreviewBy(1)}
                overallSkinScore={overallSkinScore}
                skinAgeValue={showSkinAgeSection ? skinAgeValue : null}
                skinAgeLoading={isSkinAgeLoading}
                displaySkinType={displaySkinType}
                displaySkinColor={displaySkinColor}
                summaryText={shortWhatThisMeans || (showFallbackSummary ? effectiveSummaryText : '')}
                summaryLoading={isAiContentLoading || summaryLoading}
                pdfMode={pdfCaptureMode}
                onSkinAgeInfo={() => setAboutPopup({
                  open: true,
                  title: 'Your Skin Age',
                  text: skinAgeExplanation,
                  sections: [],
                })}
                onSkinScoreInfo={() => setAboutPopup({
                  open: true,
                  title: 'Skin Score',
                  text: 'Your overall skin score reflects the combined health of visible skin parameters from your AI analysis.',
                  sections: [],
                })}
              />

              {(aqiData?.success || displayEnvTip) ? (
                <aside className="skin-report-env" aria-label="Environmental context">
                  {aqiData?.success ? (
                    <div className="skin-report-env-chips">
                      {aqiData.location ? (
                        <span className="skin-report-env-chip">
                          <span className="skin-report-env-chip-icon" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>
                          </span>
                          <strong>Location</strong> {aqiData.location}
                        </span>
                      ) : null}
                      {aqiData.temperature != null ? (
                        <span className="skin-report-env-chip">
                          <span className="skin-report-env-chip-icon" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V5a2 2 0 1 0-4 0v9.76a4 4 0 1 0 4 0z"/></svg>
                          </span>
                          <strong>Temp</strong> {aqiData.temperature}°C
                        </span>
                      ) : null}
                      {(aqiData.uv_level || aqiData.uv_index != null) ? (
                        <span className="skin-report-env-chip">
                          <span className="skin-report-env-chip-icon" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
                          </span>
                          <strong>UV</strong>{' '}
                          <span style={{ color: getUvIndexColor(aqiData.uv_index ?? 0) }}>
                            {aqiData.uv_level || getUvDescription(aqiData.uv_index)}
                          </span>
                        </span>
                      ) : null}
                      {aqiData.humidity != null ? (
                        <span className="skin-report-env-chip">
                          <span className="skin-report-env-chip-icon" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3s6 6.2 6 10a6 6 0 1 1-12 0c0-3.8 6-10 6-10z"/></svg>
                          </span>
                          <strong>Humidity</strong> {aqiData.humidity}%
                        </span>
                      ) : null}
                      {aqiData.aqi != null ? (
                        <span className="skin-report-env-chip">
                          <span className="skin-report-env-chip-icon" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h16M6 16c2-3 4-5 6-5s4 2 6 5M8 10c1.5-2 2.5-3 4-3s2.5 1 4 3"/></svg>
                          </span>
                          <strong>AQI</strong> {aqiData.aqi}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {displayEnvTip ? (
                    <p className="skin-report-env-tip">
                      <span className="skin-report-env-tip-label">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 1 4 12c-.8.7-1.2 1.6-1.3 2.5H9.3C9.2 15.6 8.8 14.7 8 14A7 7 0 0 1 12 2z"/></svg>
                        Tip
                      </span>
                      {displayEnvTip}
                    </p>
                  ) : null}
                </aside>
              ) : null}
            </div>

            {/* Progressive display: health scores, routine, treatments, products */}
            {!skinResultsFromState ? (
              <div className="skin-routine-error">
                <p>Missing required data. Please go back and complete the skin analysis.</p>
                <button onClick={handleBack}>Go back</button>
              </div>
            ) : (
              <div className="skin-report-body">
                {suggestConfigLoaded && healthScoreItems.length > 0 && (showAdvancedCharts ? radarModel : true) && (
                  <div className="skin-report-section skin-report-health-section" id="section-radar" data-pdf-page="health-scores">
                    <h2 className="skin-report-section-heading">
                      <span className="skin-report-section-num" aria-hidden="true">1</span>
                      <span className="skin-report-section-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19V5M4 19h16"/><path d="M8 15l3-4 3 2 5-7"/></svg>
                      </span>
                      Skin Health Scores
                    </h2>
                    <div className="skin-report-health-radar-row">
                      {hasBeforeAfterRadar && showAdvancedCharts && radarModel ? (
                        <div className="skin-report-radar-tabs" role="tablist" aria-label="Skin analysis report comparison tabs">
                          <button
                            type="button"
                            role="tab"
                            aria-selected={radarComparisonTab === 'before'}
                            className={`skin-report-radar-tab ${radarComparisonTab === 'before' ? 'is-active' : ''}`}
                            onClick={() => setRadarComparisonTab('before')}
                          >
                            Before
                          </button>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={radarComparisonTab === 'after'}
                            className={`skin-report-radar-tab ${radarComparisonTab === 'after' ? 'is-active' : ''}`}
                            onClick={() => setRadarComparisonTab('after')}
                          >
                            After
                          </button>
                        </div>
                      ) : null}

                      <div className="skin-report-health-radar-grid">
                        <div className="skin-report-health-scores-wrap" id="section-skin-health-scores">
                          <SkinHealthScoresPanel
                            items={healthScoreItems}
                            selectedName={selectedConcernName}
                            onSelect={handleSelectConcern}
                          />
                        </div>
                        {showAdvancedCharts && radarModel ? (
                        <div className="skin-report-radar-panel">
                          <div className="skin-report-radar-panel-header">
                            <h3 className="skin-report-radar-title">
                              {hasBeforeAfterRadar ? (radarComparisonTab === 'before' ? 'Before' : 'After') : 'Balance'}
                            </h3>
                            <button
                              type="button"
                              className="skin-report-radar-enlarge"
                              onClick={() => setIsRadarEnlargedOpen(true)}
                              aria-label="Enlarge radar chart to fullscreen view"
                              title="Expand chart"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                              </svg>
                            </button>
                          </div>
                          <div className="skin-report-radar-meta">
                            {isAfterComparisonView ? (
                              <>
                                <span>Score: {overallSkinScore != null ? `${overallSkinScore}/100` : '--'}</span>
                                <span>
                                  From {previousAnalysisDateText || '--'} to {analysisDateText || '--'}
                                  {radarDaysBetween != null ? ` (${radarDaysBetween}d)` : ''}
                                </span>
                              </>
                            ) : (
                              <>
                                <span>Score: {activeRadarScore != null ? `${activeRadarScore}/100` : '--'}</span>
                                <span>{activeRadarDate || '--'}</span>
                              </>
                            )}
                          </div>
                          <svg className="skin-routine-radar-svg skin-report-radar-svg" viewBox={`0 0 ${activeRadarModel.size} ${activeRadarModel.size}`} role="img" aria-label="Skin analysis report chart">
                            {activeRadarModel.levels.map((level) => {
                              const r = (level / 100) * activeRadarModel.radius;
                              return (
                                <circle
                                  key={`level-${level}`}
                                  cx={activeRadarModel.cx}
                                  cy={activeRadarModel.cy}
                                  r={r}
                                  fill="none"
                                  stroke="rgba(100, 116, 139, 0.22)"
                                  strokeWidth="1"
                                />
                              );
                            })}
                            {activeRadarModel.axis.map((item, idx) => (
                              <line
                                key={`axis-${idx}`}
                                x1={activeRadarModel.cx}
                                y1={activeRadarModel.cy}
                                x2={activeRadarModel.cx + Math.cos(item.angle) * activeRadarModel.radius}
                                y2={activeRadarModel.cy + Math.sin(item.angle) * activeRadarModel.radius}
                                stroke="rgba(100, 116, 139, 0.2)"
                                strokeWidth="1"
                              />
                            ))}
                            {isAfterComparisonView ? (
                              <>
                                <polygon
                                  points={activeRadarModel.beforePolygon}
                                  fill="none"
                                  stroke="rgba(249, 115, 22, 0.95)"
                                  strokeWidth="1.8"
                                  strokeDasharray="5 4"
                                />
                                <polygon
                                  points={activeRadarModel.afterPolygon}
                                  fill="none"
                                  stroke="rgba(37, 99, 235, 0.95)"
                                  strokeWidth="2.2"
                                />
                                {activeRadarModel.axis.map((item, idx) => (
                                  <g
                                    key={`compare-point-${idx}`}
                                    className="skin-routine-radar-clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openRadarConcernModal(idx)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openRadarConcernModal(idx);
                                      }
                                    }}
                                  >
                                    {item.hasBefore && item.hasAfter ? (
                                      <line
                                        x1={item.beforePointX}
                                        y1={item.beforePointY}
                                        x2={item.afterPointX}
                                        y2={item.afterPointY}
                                        stroke={
                                          item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.95)'
                                            : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.95)'
                                              : 'rgba(148, 163, 184, 0.9)'
                                        }
                                        strokeWidth="2"
                                        strokeDasharray={item.changeKind === 'stable' ? '3 3' : 'none'}
                                      />
                                    ) : null}
                                    {item.hasBefore ? <circle cx={item.beforePointX} cy={item.beforePointY} r="2.8" fill="rgba(249, 115, 22, 0.95)" /> : null}
                                    {item.hasAfter ? (
                                      <circle
                                        cx={item.afterPointX}
                                        cy={item.afterPointY}
                                        r="3.8"
                                        fill={
                                          item.changeKind === 'new' ? 'rgba(168, 85, 247, 0.98)'
                                            : item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.98)'
                                              : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.98)'
                                                : 'rgba(37, 99, 235, 0.95)'
                                        }
                                      />
                                    ) : null}
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className={`skin-routine-radar-label skin-routine-radar-label--${item.changeKind}`}
                                    >
                                      {item.label}
                                    </text>
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY + 13}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className={`skin-routine-radar-score skin-routine-radar-score--${item.changeKind}`}
                                    >
                                      {item.changeKind === 'new'
                                        ? `A:${item.afterScore != null ? `${item.afterScore}%` : '--'} New`
                                        : item.changeKind === 'missing'
                                          ? `B:${item.beforeScore != null ? `${item.beforeScore}%` : '--'} Missing`
                                          : `B:${item.beforeScore != null ? `${item.beforeScore}%` : '--'} A:${item.afterScore != null ? `${item.afterScore}%` : '--'} ${item.delta != null ? `${item.delta > 0 ? '+' : ''}${item.delta}` : ''}`}
                                    </text>
                                  </g>
                                ))}
                              </>
                            ) : (
                              <>
                                <polygon
                                  points={activeRadarModel.polygon}
                                  fill="none"
                                  stroke="rgba(37, 99, 235, 0.95)"
                                  strokeWidth="2"
                                />
                                {activeRadarModel.axis.map((item, idx) => (
                                  <g
                                    key={`point-${idx}`}
                                    className="skin-routine-radar-clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openRadarConcernModal(idx)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openRadarConcernModal(idx);
                                      }
                                    }}
                                  >
                                    <circle cx={item.pointX} cy={item.pointY} r="3.6" fill="rgba(37, 99, 235, 0.95)" />
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className="skin-routine-radar-label"
                                    >
                                      {item.label}
                                    </text>
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY + 13}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className="skin-routine-radar-score"
                                    >
                                      {item.score}%
                                    </text>
                                  </g>
                                ))}
                              </>
                            )}
                          </svg>
                          <div className="skin-routine-radar-legend" aria-label="Radar chart color legend">
                            {isAfterComparisonView ? (
                              <>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--before" />Before line</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />After line</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--improved" />Improved</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--declined" />Needs attention</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--new" />New in after</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--stable" />Stable</span>
                              </>
                            ) : (
                              <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />Current scan line</span>
                            )}
                          </div>
                        </div>
                        ) : null}
                      </div>

                    </div>
                  </div>
                )}

                {healthScoreItems.length > 0 ? (
                  <div className="skin-report-section" id="section-detailed-crops" data-pdf-page="detailed-analysis">
                    <h2 className="skin-report-section-heading">
                      <span className="skin-report-section-num" aria-hidden="true">2</span>
                      <span className="skin-report-section-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2"/></svg>
                      </span>
                      Detailed Skin Analysis
                    </h2>
                    <SkinDetailedCrops
                      items={healthScoreItems}
                      imageSrc={previewBaseImage}
                      detections={normalizeDetections(
                        Array.isArray(skinResultsFromState?.frontDetections)
                          ? skinResultsFromState.frontDetections
                          : (Array.isArray(skinResultsFromState?.detections)
                            ? skinResultsFromState.detections
                            : []),
                      )}
                      descriptions={aiConditionDescriptions}
                      skinConditions={skinResultsFromState?.skinConditions || null}
                      sessionId={sessionId}
                      token={token}
                      apiBaseUrl={API_BASE_URL}
                    />
                  </div>
                ) : null}

                {macroSkipped && !displayMacro && !isMacroLoading ? (
                  <div className="skin-report-section skin-report-macro-skipped" id="section-macro-skipped">
                    <p className="skin-report-macro-skipped__text">
                      Close-up microscope capture was skipped. Scores, routine, and recommendations below are based on your face scan only.
                    </p>
                  </div>
                ) : null}

                {displayMacro ? (
                  <div className="skin-report-section" id="section-macro-close" data-pdf-page="macro-close">
                    <h2 className="skin-report-section-heading">
                      <span className="skin-report-section-num" aria-hidden="true">2b</span>
                      <span className="skin-report-section-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2"/></svg>
                      </span>
                      Skin Close Looks
                    </h2>
                    <SkinMacroCloseLooks macro={displayMacro} pdfMode={pdfCaptureMode} />
                  </div>
                ) : isMacroLoading ? (
                  <div className="skin-report-section" id="section-macro-close" aria-busy="true">
                    <h2 className="skin-report-section-heading">
                      <span className="skin-report-section-num" aria-hidden="true">2b</span>
                      <span className="skin-report-section-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2"/></svg>
                      </span>
                      Skin Close Looks
                    </h2>
                    <div className="skin-report-loader skin-report-macro-loader">
                      <div className="loading-spinner" />
                      <span>Analyzing close-up images…</span>
                    </div>
                  </div>
                ) : null}

                {displayMacro && hasMacroScores ? (
                  <div className="skin-report-section" id="section-macro-analysis" data-pdf-page="macro-analysis">
                    <h2 className="skin-report-section-heading">
                      <span className="skin-report-section-num" aria-hidden="true">2c</span>
                      <span className="skin-report-section-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h16M7 16l3-8 3 5 3-3 3 6"/><circle cx="18" cy="6" r="3"/></svg>
                      </span>
                      Macro Skin Analysis (Microscope)
                    </h2>
                    <SkinMacroAnalysisReport macro={displayMacro} pdfMode={pdfCaptureMode} />
                  </div>
                ) : isMacroLoading ? (
                  <div className="skin-report-section" id="section-macro-analysis" aria-busy="true">
                    <h2 className="skin-report-section-heading">
                      <span className="skin-report-section-num" aria-hidden="true">2c</span>
                      <span className="skin-report-section-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h16M7 16l3-8 3 5 3-3 3 6"/><circle cx="18" cy="6" r="3"/></svg>
                      </span>
                      Macro Skin Analysis (Microscope)
                    </h2>
                    <div className="skin-report-loader skin-report-macro-loader">
                      <div className="loading-spinner" />
                      <span>Running microscope analysis…</span>
                    </div>
                  </div>
                ) : null}

                {(healthScoreItems.length > 0) ? (
                  <div className="skin-report-section skin-report-priority-routine" id="section-priority-routine" data-pdf-page="priority-routine">
                    <div className="skin-report-priority-routine-grid">
                      <div className="skin-report-priority-col" id="section-priority">
                        <h2 className="skin-report-section-heading">
                          <span className="skin-report-section-num" aria-hidden="true">3</span>
                          <span className="skin-report-section-icon" aria-hidden="true">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19h16M7 16l3-8 3 5 3-3 3 6"/></svg>
                          </span>
                          Skin Concerns Priority
                        </h2>
                        <SkinConcernsPriority
                          items={healthScoreItems}
                          improvementPotential={improvementPotential}
                        />
                      </div>
                      <div className="skin-report-routine-col-wrap" id="section-routine">
                        <h2 className="skin-report-section-heading">
                          <span className="skin-report-section-num" aria-hidden="true">4</span>
                          <span className="skin-report-section-icon" aria-hidden="true">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v4l2 3v9a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-9l2-3V3z"/><path d="M9 10h6"/></svg>
                          </span>
                          Recommended Routine
                        </h2>
                        <div className="skin-report-routine-tips-row">
                          <SkinReportRoutine
                            morningProducts={recommendedProducts.morning}
                            eveningProducts={recommendedProducts.evening}
                            morningSteps={routine?.morning || []}
                            eveningSteps={routine?.evening || []}
                            resolveImage={resolveProductImageUrl}
                            loading={summaryLoading && !(routine?.morning?.length || recommendedProducts.morning?.length)}
                          />
                          <div className="skin-report-tips-panel">
                            <h3 className="skin-report-section-label">
                              <span className="skin-report-inline-icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z"/></svg>
                              </span>
                              Tips for You
                            </h3>
                            <SkinReportTips
                              tips={displayTips}
                              loading={summaryLoading && !reportTips.length}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="skin-report-section" id="section-routine" data-pdf-page="routine">
                    <h2 className="skin-report-section-heading">
                      <span className="skin-report-section-num" aria-hidden="true">4</span>
                      <span className="skin-report-section-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v4l2 3v9a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-9l2-3V3z"/><path d="M9 10h6"/></svg>
                      </span>
                      Recommended Routine
                    </h2>
                    <div className="skin-report-routine-tips-row">
                      <SkinReportRoutine
                        morningProducts={recommendedProducts.morning}
                        eveningProducts={recommendedProducts.evening}
                        morningSteps={routine?.morning || []}
                        eveningSteps={routine?.evening || []}
                        resolveImage={resolveProductImageUrl}
                        loading={summaryLoading && !(routine?.morning?.length || recommendedProducts.morning?.length)}
                      />
                      <div className="skin-report-tips-panel">
                        <h3 className="skin-report-section-label">
                          <span className="skin-report-inline-icon" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z"/></svg>
                          </span>
                          Tips for You
                        </h3>
                        <SkinReportTips
                          tips={displayTips}
                          loading={summaryLoading && !reportTips.length}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {suggestConfigLoaded && serviceSuggestionEnabled && (
                  <div className="skin-report-section skin-report-reco-section" id="section-treatments" data-pdf-page="treatments">
                    <h2 className="skin-report-section-heading">
                      <span className="skin-report-section-num" aria-hidden="true">5</span>
                      <span className="skin-report-section-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v4M8 7h8l1 4H7l1-4z"/><path d="M7 11v8a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-8"/></svg>
                      </span>
                      Personalized Services
                    </h2>
                    <div className="skin-report-reco">
                      {isTreatmentsLoading ? (
                        <div className="skin-routine-summary-loading">
                          <div className="loading-spinner"></div>
                          <p className="skin-routine-summary-loading-text">Generating recommended services...</p>
                        </div>
                      ) : recommendedTreatments.length > 0 ? (
                        <div className="skin-routine-treatments-grid">
                          {recommendedTreatments.map((item, idx) => {
                            const title = item?.treatment || item?.name || `Treatment ${idx + 1}`;
                            const cartKey = treatmentRowCartKey(item, idx);
                            const cartQuantity = treatmentCartItems[cartKey]?.quantity || 0;
                            const recommendationLine = getRecommendationLine(item)
                              .replace(/\s*[—–-]\s*aligns with \d+ of your selected goal\(s\)/i, '')
                              .replace(/^aligns with \d+ selected goal\(s\)$/i, '')
                              .trim();
                            const aboutText = item?.about_treatment || item?.description || '';
                            const majorUspPlain = item?.major_usp ? stripHtml(item.major_usp) : '';
                            const serviceType = String(item?.service_type || '').replace('_', ' ');
                            const category = item?.category || '';
                            const hasNetPrice = toNumberOrNull(item?.net_price) != null;
                            const hasAmountPrice = toNumberOrNull(item?.amount) != null;
                            const sessionsForPrice = Math.max(1, cartQuantity || 0);
                            const priceText = String(item?.price || '').trim();
                            const hasAnyPrice = hasNetPrice || hasAmountPrice || Boolean(priceText);
                            const net = toNumberOrNull(item?.net_price);
                            const sale = toNumberOrNull(item?.sale_price);
                            const amount = toNumberOrNull(item?.amount);
                            return (
                              <div key={`${title}-${idx}`} className="skin-routine-treatment-card skin-routine-treatment-card--text">
                                <div className="skin-routine-treatment-body">
                                  <div className="skin-routine-treatment-text-head">
                                    <div className="skin-routine-treatment-title">{title}</div>
                                    {(serviceType || category) ? (
                                      <div className="skin-routine-treatment-meta-chips">
                                        {serviceType ? (
                                          <span className="skin-routine-treatment-chip">{serviceType}</span>
                                        ) : null}
                                        {category ? (
                                          <span className="skin-routine-treatment-chip">{category}</span>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>

                                  {recommendationLine ? (
                                    <div className="skin-routine-treatment-row">
                                      <span className="skin-routine-treatment-mini-heading">Why recommended</span>
                                      <div className="skin-routine-treatment-why-text">{recommendationLine}</div>
                                    </div>
                                  ) : null}

                                  {majorUspPlain || aboutText ? (
                                    <div className="skin-routine-treatment-row">
                                      <span className="skin-routine-treatment-mini-heading">About</span>
                                      <div className="skin-routine-treatment-usp-text">
                                        {majorUspPlain || aboutText}
                                      </div>
                                    </div>
                                  ) : null}

                                  {item?.conditions?.length > 0 ? (
                                    <div className="skin-routine-treatment-tags-area">
                                      <div className="skin-routine-treatment-tags-wrap">
                                        <span className="skin-routine-treatment-mini-heading">Addresses</span>
                                        <div className="skin-routine-treatment-tags">
                                          {item.conditions.map((c, i) => (
                                            <span key={`${c}-${i}`} className="skin-routine-treatment-tag skin-routine-treatment-tag--condition">{c}</span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}

                                  <button
                                    type="button"
                                    className="skin-routine-treatment-view-details"
                                    onClick={() => openTreatmentAboutPopup(item)}
                                  >
                                    View details
                                  </button>
                                </div>

                                <div className="skin-routine-treatment-footer">
                                  <div className="skin-routine-treatment-price-block">
                                    {hasAnyPrice ? (
                                      hasNetPrice || hasAmountPrice ? (
                                        <span className="skin-routine-treatment-price">
                                          {formatPrice((sale ?? net ?? amount ?? 0) * sessionsForPrice)}
                                        </span>
                                      ) : (
                                        <span className="skin-routine-treatment-price">{priceText}</span>
                                      )
                                    ) : (
                                      <span className="skin-routine-treatment-price-free">Ask front desk</span>
                                    )}
                                  </div>
                                  <div className="skin-routine-treatment-cta">
                                    <ProductCartCounter
                                      key={`routine-tc-${cartKey}`}
                                      product={item}
                                      onAddToCart={(t, quantity) => handleAddTreatmentToCart(t, quantity, cartKey)}
                                      disabled={false}
                                      initialQuantity={cartQuantity}
                                      quantityLabel={hasAmountPrice ? 'Sessions' : 'Qty'}
                                      addLabel="Select Service"
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="skin-report-treatments-empty">
                          <p className="skin-routine-summary-text">No service recommendations available yet.</p>
                          <button
                            type="button"
                            className="skin-report-treatments-retry"
                            onClick={handleRetryTreatments}
                            disabled={isTreatmentsLoading}
                          >
                            Retry services
                          </button>
                        </div>
                      )}

                    </div>
                  </div>
                )}

                {/* Recommended products are shown on stylist lobby / front desk only — not on this routine page. */}
                {false && suggestConfigLoaded && productSuggestionEnabled && getTotalTreatmentCartItems() > 0 ? (
                  <div className="skin-report-section skin-report-reco-section" id="section-products" data-pdf-page="products">
                    <h2 className="skin-report-section-heading">
                      <span className="skin-report-section-num" aria-hidden="true">6</span>
                      <span className="skin-report-section-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="3" width="10" height="18" rx="2"/><path d="M10 7h4"/></svg>
                      </span>
                      AI Recommended Products for You
                    </h2>
                    <div className="skin-report-reco skin-report-products-reco">
                      {isProductsLoading && recommendedProducts.morning.length === 0 && recommendedProducts.evening.length === 0 ? (
                        <div className="skin-routine-summary-loading">
                          <div className="loading-spinner"></div>
                          <p className="skin-routine-summary-loading-text">Generating recommended products...</p>
                        </div>
                      ) : (recommendedProducts.morning.length > 0 || recommendedProducts.evening.length > 0) ? (
                        <>
                          <div className="skin-report-products-row">
                            {[...recommendedProducts.morning, ...recommendedProducts.evening].map((product, idx) => {
                              const title = product?.product_name || product?.name || `Product ${idx + 1}`;
                              const productId = product?.product_id || product?.id || `routine_product_${title}_${idx}`;
                              const cartQuantity = productCartItems[productId]?.quantity || 0;
                              const imageUrl = resolveProductImageUrl(product?.image_url);
                              const productHref = resolveProductHref(product);
                              const showProductLinkCta = productLinkEnabled && isValidProductUrl(productHref);
                              const matchPct = getProductMatchPercent(product, idx);
                              return (
                                <article key={`${productId}-${idx}`} className="skin-report-product-card">
                                  <div className="skin-report-product-media">
                                    {imageUrl ? (
                                      <img
                                        src={imageUrl}
                                        alt={title}
                                        className="skin-report-product-image"
                                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                                      />
                                    ) : (
                                      <div className="skin-report-product-image-fallback" aria-hidden="true" />
                                    )}
                                  </div>
                                  <div className="skin-report-product-body">
                                    <h3 className="skin-report-product-name">{title}</h3>
                                    <p className="skin-report-product-match">{matchPct}% Match</p>
                                    <div className="skin-report-product-actions">
                                      <ProductCartCounter
                                          key={`routine-pc-${productId}`}
                                          product={product}
                                          onAddToCart={(p, quantity) => handleAddProductToCart(p, quantity, productId)}
                                          disabled={false}
                                          initialQuantity={cartQuantity}
                                          quantityLabel="Qty"
                                          addLabel="Add to Cart"
                                        />
                                      {showProductLinkCta ? (
                                        <button
                                          type="button"
                                          className="skin-report-product-view"
                                          onClick={() => handleViewProduct(product, productHref)}
                                        >
                                          View Product
                                        </button>
                                      ) : (
                                        <span className="skin-report-product-view skin-report-product-view--muted">
                                          Front desk
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                          <p className="skin-report-products-note">
                            Recommended from your skin analysis. Confirm selections for the front desk.
                          </p>
                        </>
                      ) : (
                        <div className="skin-routine-summary-error-wrap">
                          <p className="skin-routine-summary-text">No product recommendations available yet.</p>
                          <button
                            type="button"
                            className="skin-action-button skin-action-button-secondary"
                            onClick={fetchStoredProducts}
                            disabled={isProductsLoading}
                          >
                            {isProductsLoading ? 'LOADING...' : 'REFRESH PRODUCTS'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {(getTotalTreatmentCartItems() > 0) ? (
                  <div className="skin-routine-next-treatments-inline" data-pdf-exclude="true">
                    <button
                      type="button"
                      className="skin-action-button skin-action-button-secondary skin-routine-home-inline-button"
                      onClick={handleOpenHomeConfirmPopup}
                    >
                      Home
                    </button>
                    {getTotalTreatmentCartItems() > 0 ? (
                      <button
                        type="button"
                        className="skin-action-button skin-routine-continue-treatments-button"
                        disabled={confirmBusy}
                        onClick={handleConfirmServices}
                      >
                        {confirmBusy ? 'CONFIRMING…' : `CONFIRM SERVICES (${getTotalTreatmentCartItems()})`}
                      </button>
                    ) : null}
                    {confirmNotice ? (
                      <p className="skin-routine-summary-text" role="status">{confirmNotice}</p>
                    ) : null}
                  </div>
                ) : null}

              </div>
            )}
          </section>

        </div>
      </main>

      {/* About Treatment Popup */}
      {aboutPopup.open && (
        <div className="skin-routine-about-popup-backdrop" onClick={closeAboutPopup}>
          <div className="skin-routine-about-popup" onClick={(e) => e.stopPropagation()}>
            <div className="skin-routine-about-popup-header">
              <h3 className="skin-routine-about-popup-title">{aboutPopup.title}</h3>
              <button
                type="button"
                className="skin-routine-about-popup-close"
                onClick={closeAboutPopup}
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="skin-routine-about-popup-body">
              {Array.isArray(aboutPopup.sections) && aboutPopup.sections.length > 0 ? (
                <div className="skin-routine-about-popup-sections">
                  {aboutPopup.sections.map((section) => (
                    <div key={section.label} className="skin-routine-about-popup-section">
                      <h4 className="skin-routine-about-popup-section-label">{section.label}</h4>
                      {Array.isArray(section.list) ? (
                        <div className="skin-routine-treatment-tags">
                          {section.list.map((tag) => (
                            <span
                              key={`${section.label}-${tag}`}
                              className="skin-routine-treatment-tag skin-routine-treatment-tag--condition"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="skin-routine-about-popup-section-text">{section.value}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                (() => {
                  const raw = aboutPopup.text == null ? '' : String(aboutPopup.text);
                  const isHtml = /<[a-z][\s\S]*>/i.test(raw);
                  const html = isHtml
                    ? sanitizeHtml(raw)
                    : sanitizeHtml(
                        raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'),
                      );
                  return <div dangerouslySetInnerHTML={{ __html: html }} />;
                })()
              )}
            </div>
          </div>
        </div>
      )}

      {showHomeConfirmPopup && (
        <div className="skin-routine-home-confirm-backdrop" onClick={handleCloseHomeConfirmPopup}>
          <div className="skin-routine-home-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="skin-routine-home-confirm-header">
              <h3 className="skin-routine-home-confirm-title">Go To Home?</h3>
            </div>
            <p className="skin-routine-home-confirm-text">
              Are you sure you want to go to Home? Any unsaved selection updates on this page may be lost.
            </p>
            <div className="skin-routine-home-confirm-actions">
              <button
                type="button"
                className="skin-action-button skin-action-button-secondary skin-routine-home-confirm-cancel"
                onClick={handleCloseHomeConfirmPopup}
              >
                Stay Here
              </button>
              <button
                type="button"
                className="skin-action-button skin-routine-home-confirm-continue"
                onClick={handleConfirmGoHome}
              >
                Yes, Go Home
              </button>
            </div>
          </div>
        </div>
      )}

      {/* USP Popup */}
      {uspPopup.open && (() => {
        const raw = String(uspPopup.text);
        const isHtml = /<[a-z][\s\S]*>/i.test(raw);
        const html = isHtml ? sanitizeHtml(raw) : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
        return (
          <div className="product-usp-popup-overlay" onClick={handleCloseUspPopup}>
            <div
              className="product-usp-popup"
              style={{ top: `${uspPopup.position.top}px`, left: `${uspPopup.position.left}px`, width: `${Math.max(uspPopup.position.width || 300, 300)}px` }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="product-usp-popup-content">
                <button className="product-usp-popup-close" onClick={handleCloseUspPopup} aria-label="Close">├ù</button>
                {uspPopup.heading && <h3 className="product-usp-popup-heading">{uspPopup.heading}</h3>}
                <div className="product-usp-popup-text" dangerouslySetInnerHTML={{ __html: html }} />
              </div>
            </div>
          </div>
        );
      })()}

      {radarConcernModal.open && activeRadarConcernDetail && (
        <div className="skin-routine-radar-modal-backdrop" onClick={closeRadarConcernModal}>
          <div
            className="skin-routine-radar-modal"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => {
              radarModalTouchStartXRef.current = e.touches?.[0]?.clientX ?? null;
            }}
            onTouchEnd={(e) => {
              const startX = radarModalTouchStartXRef.current;
              const endX = e.changedTouches?.[0]?.clientX ?? null;
              radarModalTouchStartXRef.current = null;
              if (startX == null || endX == null) return;
              const diff = endX - startX;
              if (Math.abs(diff) < 40) return;
              if (diff < 0) stepRadarConcernModal(1);
              else stepRadarConcernModal(-1);
            }}
          >
            <div className="skin-routine-radar-modal-header">
              <h3 className="skin-routine-radar-modal-title">{activeRadarConcernDetail.label}</h3>
              <button type="button" className="skin-routine-radar-modal-close" onClick={closeRadarConcernModal} aria-label="Close concern details">×</button>
            </div>
            <p className="skin-routine-radar-modal-subtitle">
              {activeRadarConcernDetail.beforeScore != null && activeRadarConcernDetail.afterScore != null
                ? `Before ${activeRadarConcernDetail.beforeScore}% -> After ${activeRadarConcernDetail.afterScore}%`
                : activeRadarConcernDetail.afterScore != null
                  ? `After ${activeRadarConcernDetail.afterScore}%`
                  : `Before ${activeRadarConcernDetail.beforeScore != null ? `${activeRadarConcernDetail.beforeScore}%` : '--'}`}
            </p>
            <p className="skin-routine-radar-modal-subtitle skin-routine-radar-modal-dates">
              From {previousAnalysisDateText || '--'} to {analysisDateText || '--'}
            </p>
            <svg className="skin-routine-radar-modal-linechart" viewBox="0 0 320 160" role="img" aria-label={`${activeRadarConcernDetail.label} trend`}>
              <line x1="42" y1="20" x2="42" y2="132" stroke="rgba(100, 116, 139, 0.35)" strokeWidth="1.2" />
              <line x1="42" y1="132" x2="286" y2="132" stroke="rgba(100, 116, 139, 0.35)" strokeWidth="1.2" />
              {[0, 25, 50, 75, 100].map((tick) => {
                const y = 132 - (tick / 100) * 108;
                return <line key={`modal-tick-${tick}`} x1="42" y1={y} x2="286" y2={y} stroke="rgba(148, 163, 184, 0.16)" strokeWidth="1" />;
              })}
              {activeRadarConcernDetail.beforeScore != null && activeRadarConcernDetail.afterScore != null ? (
                <>
                  <line
                    x1="90"
                    y1={132 - (activeRadarConcernDetail.beforeScore / 100) * 108}
                    x2="240"
                    y2={132 - (activeRadarConcernDetail.afterScore / 100) * 108}
                    stroke={activeRadarConcernDetail.delta != null && activeRadarConcernDetail.delta >= 0 ? 'rgba(22,163,74,0.95)' : 'rgba(220,38,38,0.95)'}
                    strokeWidth="3"
                  />
                  <circle cx="90" cy={132 - (activeRadarConcernDetail.beforeScore / 100) * 108} r="5" fill="rgba(71, 85, 105, 0.95)" />
                  <circle cx="240" cy={132 - (activeRadarConcernDetail.afterScore / 100) * 108} r="5" fill="rgba(15, 118, 110, 0.95)" />
                </>
              ) : activeRadarConcernDetail.afterScore != null ? (
                <circle cx="240" cy={132 - (activeRadarConcernDetail.afterScore / 100) * 108} r="5" fill="rgba(15, 118, 110, 0.95)" />
              ) : (
                <circle cx="90" cy={132 - ((activeRadarConcernDetail.beforeScore || 0) / 100) * 108} r="5" fill="rgba(71, 85, 105, 0.95)" />
              )}
              <text x="90" y="148" textAnchor="middle" className="skin-routine-radar-modal-axis">{previousAnalysisDateText || 'Before'}</text>
              <text x="240" y="148" textAnchor="middle" className="skin-routine-radar-modal-axis">{analysisDateText || 'After'}</text>
            </svg>
            <div className="skin-routine-radar-modal-legend">
              <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--before" />Before</span>
              <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />After</span>
              <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--improved" />Improved line</span>
              <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--declined" />Decline line</span>
            </div>
            <div className="skin-routine-radar-modal-actions">
              <button type="button" className="skin-routine-radar-modal-nav" onClick={() => stepRadarConcernModal(-1)}>Prev</button>
              <button type="button" className="skin-routine-radar-modal-nav" onClick={() => stepRadarConcernModal(1)}>Next</button>
            </div>
          </div>
        </div>
      )}

      {/* Enlarged Radar Chart Modal */}
      {isRadarEnlargedOpen && (
        <div className="skin-routine-radar-enlarged-backdrop" onClick={() => setIsRadarEnlargedOpen(false)}>
          <div
            className="skin-routine-radar-enlarged-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="skin-routine-radar-enlarged-close"
              onClick={() => setIsRadarEnlargedOpen(false)}
              aria-label="Close enlarged radar view"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="skin-routine-radar-enlarged-content">
              {/* Tabs for Before/After */}
              {hasBeforeAfterRadar && (
                <div className="skin-routine-radar-enlarged-tabs" role="tablist" aria-label="Enlarged skin analysis report comparison tabs">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={radarComparisonTab === 'before'}
                    className={`skin-routine-radar-enlarged-tab ${radarComparisonTab === 'before' ? 'active' : ''}`}
                    onClick={() => setRadarComparisonTab('before')}
                  >
                    Before
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={radarComparisonTab === 'after'}
                    className={`skin-routine-radar-enlarged-tab ${radarComparisonTab === 'after' ? 'active' : ''}`}
                    onClick={() => setRadarComparisonTab('after')}
                  >
                    After
                  </button>
                </div>
              )}
              
              <h3 className="skin-routine-radar-enlarged-title">
                {hasBeforeAfterRadar ? (radarComparisonTab === 'before' ? 'Before Skin Analysis Report' : 'After Skin Analysis Report') : 'Skin Analysis Report'}
              </h3>
              <div className="skin-routine-radar-enlarged-info">
                {isAfterComparisonView ? (
                  <>
                    <span>Score: {overallSkinScore != null ? `${overallSkinScore}/100` : '--'}</span>
                    <span>{previousAnalysisDateText || '--'} → {analysisDateText || '--'}</span>
                  </>
                ) : (
                  <>
                    <span>Score: {activeRadarScore != null ? `${activeRadarScore}/100` : '--'}</span>
                    <span>{activeRadarDate || '--'}</span>
                  </>
                )}
              </div>
              <div className="skin-routine-radar-enlarged-chart-wrapper">
                <svg className="skin-routine-radar-enlarged-svg" viewBox={`0 0 ${activeRadarModel.size} ${activeRadarModel.size}`} role="img" aria-label="Enlarged skin analysis report chart">
                  {/* Grid Circles */}
                  {activeRadarModel.levels.map((level) => {
                    const r = (level / 100) * activeRadarModel.radius;
                    return (
                      <circle
                        key={`level-${level}`}
                        cx={activeRadarModel.cx}
                        cy={activeRadarModel.cy}
                        r={r}
                        fill="none"
                        stroke="rgba(100, 116, 139, 0.22)"
                        strokeWidth="1"
                      />
                    );
                  })}
                  {/* Axis Lines */}
                  {activeRadarModel.axis.map((item, idx) => (
                    <line
                      key={`axis-${idx}`}
                      x1={activeRadarModel.cx}
                      y1={activeRadarModel.cy}
                      x2={activeRadarModel.cx + Math.cos(item.angle) * activeRadarModel.radius}
                      y2={activeRadarModel.cy + Math.sin(item.angle) * activeRadarModel.radius}
                      stroke="rgba(100, 116, 139, 0.2)"
                      strokeWidth="1"
                    />
                  ))}
                  {/* Polygons */}
                  {isAfterComparisonView ? (
                    <>
                      <polygon
                        points={activeRadarModel.beforePolygon}
                        fill="none"
                        stroke="rgba(249, 115, 22, 0.95)"
                        strokeWidth="2.2"
                        strokeDasharray="5 4"
                      />
                      <polygon
                        points={activeRadarModel.afterPolygon}
                        fill="none"
                        stroke="rgba(37, 99, 235, 0.95)"
                        strokeWidth="2.6"
                      />
                    </>
                  ) : (
                    <polygon
                      points={activeRadarModel.polygon}
                      fill="none"
                      stroke="rgba(37, 99, 235, 0.95)"
                      strokeWidth="2.6"
                    />
                  )}
                  {/* Data Points and Labels */}
                  {isAfterComparisonView ? (
                    <>
                      {activeRadarModel.axis.map((item, idx) => (
                        <g key={`compare-point-${idx}`}>
                          {/* Connection line between before/after */}
                          {item.hasBefore && item.hasAfter ? (
                            <line
                              x1={item.beforePointX}
                              y1={item.beforePointY}
                              x2={item.afterPointX}
                              y2={item.afterPointY}
                              stroke={
                                item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.95)'
                                  : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.95)'
                                    : 'rgba(148, 163, 184, 0.9)'
                              }
                              strokeWidth="2.4"
                              strokeDasharray={item.changeKind === 'stable' ? '3 3' : 'none'}
                            />
                          ) : null}
                          {/* Before point */}
                          {item.hasBefore ? <circle cx={item.beforePointX} cy={item.beforePointY} r="3.4" fill="rgba(249, 115, 22, 0.95)" /> : null}
                          {/* After point */}
                          {item.hasAfter ? (
                            <circle
                              cx={item.afterPointX}
                              cy={item.afterPointY}
                              r="4.2"
                              fill={
                                item.changeKind === 'new' ? 'rgba(168, 85, 247, 0.98)'
                                  : item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.98)'
                                    : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.98)'
                                      : 'rgba(37, 99, 235, 0.95)'
                              }
                            />
                          ) : null}
                          {/* Label */}
                          <text
                            x={item.labelX + (item.labelOffsetX || 0)}
                            y={item.labelY}
                            textAnchor={item.labelAnchor || 'middle'}
                            className={`skin-routine-radar-label skin-routine-radar-label--${item.changeKind}`}
                          >
                            {item.label}
                          </text>
                          {/* Score */}
                          <text
                            x={item.labelX + (item.labelOffsetX || 0)}
                            y={item.labelY + 15}
                            textAnchor={item.labelAnchor || 'middle'}
                            className={`skin-routine-radar-score skin-routine-radar-score--${item.changeKind}`}
                          >
                            {item.changeKind === 'new'
                              ? `A:${item.afterScore != null ? `${item.afterScore}%` : '--'} New`
                              : item.changeKind === 'missing'
                                ? `B:${item.beforeScore != null ? `${item.beforeScore}%` : '--'} Missing`
                                : `B:${item.beforeScore != null ? `${item.beforeScore}%` : '--'} A:${item.afterScore != null ? `${item.afterScore}%` : '--'} ${item.delta != null ? `${item.delta > 0 ? '+' : ''}${item.delta}` : ''}`}
                          </text>
                        </g>
                      ))}
                    </>
                  ) : (
                    <>
                      {activeRadarModel.axis.map((item, idx) => (
                        <g key={`point-${idx}`}>
                          {/* Point */}
                          <circle cx={item.pointX} cy={item.pointY} r="4" fill="rgba(37, 99, 235, 0.95)" />
                          {/* Label */}
                          <text
                            x={item.labelX + (item.labelOffsetX || 0)}
                            y={item.labelY}
                            textAnchor={item.labelAnchor || 'middle'}
                            className="skin-routine-radar-label"
                          >
                            {item.label}
                          </text>
                          {/* Score */}
                          <text
                            x={item.labelX + (item.labelOffsetX || 0)}
                            y={item.labelY + 15}
                            textAnchor={item.labelAnchor || 'middle'}
                            className="skin-routine-radar-score"
                          >
                            {item.score}%
                          </text>
                        </g>
                      ))}
                    </>
                  )}
                </svg>
              </div>
              {/* Legend */}
              <div className="skin-routine-radar-legend" aria-label="Radar chart color legend">
                {isAfterComparisonView ? (
                  <>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--before" />Before line</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />After line</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--improved" />Improved</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--declined" />Needs attention</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--new" />New in after</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--stable" />Stable</span>
                  </>
                ) : (
                  <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />Current scan line</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showBackToTop && (
        <button
          type="button"
          className="skin-routine-back-to-top"
          onClick={handleScrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 15l-6-6-6 6" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default SkinRoutinePage;
